import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { parse } from 'csv-parse/browser/esm/sync';
import jsPDF from 'jspdf';
import './MasterPlantList.css';
import { coerceImageUrl } from './utils/imageUrl';
import { SYMBOL_OPTIONS } from './symbols/registry';
import {
  PROJECT_CSV_FIELD_ORDER,
  emptyProjectCsvColumnMapping,
  suggestProjectCsvColumnMapping,
  buildMasterListCsvRow
} from './utils/projectCsvImportWizard';
import { planZonesApi } from './handoff/zonesApi';
import { GARDEN_SUN_EXPOSURE_OPTIONS } from './garden/constants';

const CARE_FIELD_GROUPS = [
  {
    title: 'Basics / Care',
    fields: [
      ['sun_needs', 'Sun'],
      ['water_needs', 'Water'],
      ['soil_preference', 'Soil'],
      ['hardiness_zone', 'Hardiness'],
      ['mature_height', 'Mature height'],
      ['mature_width', 'Mature width'],
      ['bloom_color', 'Bloom colour'],
      ['bloom_month_start', 'Bloom starts'],
      ['bloom_month_end', 'Bloom ends'],
      ['foliage_notes', 'Foliage notes'],
      ['is_evergreen', 'Evergreen'],
      ['is_pet_safe', 'Pet safe'],
      ['is_pollinator_friendly', 'Pollinator friendly'],
      ['is_native', 'Native'],
      ['is_drought_tolerant', 'Drought tolerant']
    ]
  },
  {
    title: 'Seasonal Tasks',
    fields: [
      ['prune_month', 'Prune month'],
      ['prune_notes', 'Prune notes'],
      ['fertilise_month', 'Fertilize month'],
      ['divide_month', 'Divide month'],
      ['mulch_month', 'Mulch month'],
      ['dormancy_start', 'Dormancy starts'],
      ['dormancy_end', 'Dormancy ends'],
      ['spring_tasks', 'Spring tasks'],
      ['summer_tasks', 'Summer tasks'],
      ['fall_tasks', 'Fall tasks'],
      ['winter_tasks', 'Winter tasks']
    ]
  },
  {
    title: 'Notes',
    fields: [
      ['care_notes', 'Care notes']
    ]
  }
];

const CARE_FIELD_LABELS = CARE_FIELD_GROUPS
  .flatMap((group) => group.fields)
  .reduce((acc, [key, label]) => ({ ...acc, [key]: label }), {});

const CARE_FIELD_KEYS = Object.keys(CARE_FIELD_LABELS);

const CARE_BOOLEAN_FIELDS = new Set([
  'is_evergreen',
  'is_pet_safe',
  'is_pollinator_friendly',
  'is_native',
  'is_drought_tolerant'
]);

const CARE_MONTH_FIELDS = new Set([
  'bloom_month_start',
  'bloom_month_end',
  'prune_month',
  'fertilise_month',
  'divide_month',
  'mulch_month',
  'dormancy_start',
  'dormancy_end'
]);

const formatCareValue = (key, value) => {
  if (CARE_BOOLEAN_FIELDS.has(key)) return value ? 'Yes' : 'No';
  return String(value);
};

const getCareReviewStatus = (item) => {
  if (!item?.ai_filled_at) {
    return {
      key: 'none',
      label: 'No care info',
      title: 'Garden App care has not been generated yet.',
      style: {
        background: '#f3f4f6',
        borderColor: '#d1d5db',
        color: '#4b5563'
      }
    };
  }
  if (item?.care_reviewed_at) {
    return {
      key: 'reviewed',
      label: 'Reviewed',
      title: `Designer reviewed ${new Date(item.care_reviewed_at).toLocaleDateString()}.`,
      style: {
        background: '#dcfce7',
        borderColor: '#86efac',
        color: '#166534'
      }
    };
  }
  return {
    key: 'needs-review',
    label: 'Needs review',
    title: 'AI care info is filled, but a designer has not marked it reviewed yet.',
    style: {
      background: '#fef3c7',
      borderColor: '#fbbf24',
      color: '#92400e'
    }
  };
};

function CareReviewBadge({ item, onNeedsReviewClick }) {
  const status = getCareReviewStatus(item);
  const canOpenReview = status.key === 'needs-review' && typeof onNeedsReviewClick === 'function';
  const Component = canOpenReview ? 'button' : 'span';
  return (
    <Component
      type={canOpenReview ? 'button' : undefined}
      onClick={canOpenReview ? onNeedsReviewClick : undefined}
      title={canOpenReview ? `${status.title} Click to view/edit care info.` : status.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 7px',
        borderRadius: '999px',
        border: `1px solid ${status.style.borderColor}`,
        background: status.style.background,
        color: status.style.color,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        cursor: canOpenReview ? 'pointer' : 'default'
      }}
    >
      {status.label}
    </Component>
  );
}

// Renders AI-filled care and seasonal detail fields for a master list item.
function CareDetailPanel({ item, onEdit, onMarkReviewed, markingReviewed }) {
  if (!item) return null;
  const has = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  const reviewStatus = getCareReviewStatus(item);
  const groups = CARE_FIELD_GROUPS
    .map((group) => ({
      ...group,
      fields: group.fields.filter(([key]) => has(item[key]) || CARE_BOOLEAN_FIELDS.has(key) && item[key] === false)
    }))
    .filter((group) => group.fields.length > 0);

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 max-w-[calc(100vw-3rem)]">
        <div style={{ color: '#6b7280', fontSize: '13px' }}>
          No care info yet. Click the ✨ button to let AI suggest care &amp; seasonal details for this plant.
        </div>
        <button type="button" onClick={onEdit} className="px-3 py-2 bg-purple-700 text-white rounded hover:bg-purple-800 text-sm font-semibold shadow-sm">
          View/Edit care info
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[calc(100vw-3rem)] min-w-0">
      <div className="flex flex-col items-start gap-3 mb-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold text-gray-900">Care information</div>
            <CareReviewBadge item={item} />
          </div>
          {item.ai_filled_at && (
            <div className="text-xs text-gray-500">
              AI-filled {new Date(item.ai_filled_at).toLocaleString()}. Edit any field before sending to the homeowner.
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onEdit} className="px-3 py-2 bg-purple-700 text-white rounded hover:bg-purple-800 text-sm font-semibold shadow-sm">
            View/Edit care info
          </button>
          <button
            type="button"
            onClick={onMarkReviewed}
            disabled={!item.ai_filled_at || reviewStatus.key === 'reviewed' || markingReviewed}
            className={`px-3 py-2 rounded text-sm font-semibold border ${
              reviewStatus.key === 'reviewed'
                ? 'bg-green-100 text-green-800 border-green-200'
                : 'bg-white text-green-700 border-green-300 hover:bg-green-50'
            } disabled:opacity-60 disabled:cursor-not-allowed`}
            title={reviewStatus.key === 'reviewed' ? 'Designer has reviewed this care info.' : 'Mark this AI-filled care info as reviewed by the designer.'}
          >
            {markingReviewed ? 'Saving…' : reviewStatus.key === 'reviewed' ? 'Reviewed' : 'Mark reviewed'}
          </button>
        </div>
      </div>
      <div className="space-y-4">
        {groups.map((group) => (
          <section key={group.title} className="rounded-lg border border-gray-200 bg-white p-3">
            <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">{group.title}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.fields.map(([key, label]) => (
                <div key={key} className={key === 'care_notes' || key.endsWith('_tasks') || key.endsWith('_notes') ? 'sm:col-span-2 lg:col-span-3' : ''}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                  <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">{formatCareValue(key, item[key])}</div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function MasterPlantList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const didAutoEditRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [project, setProject] = useState(null);
  const [designerSymbols, setDesignerSymbols] = useState([]);
  const allSymbolOptions = useMemo(() => {
    const builtIn = Array.isArray(SYMBOL_OPTIONS) ? SYMBOL_OPTIONS : [];
    const builtInIds = new Set(builtIn.map((o) => String(o.id)));
    const custom = (Array.isArray(designerSymbols) ? designerSymbols : [])
      .map((s) => ({
        id: String(s?.slug || '').trim().toLowerCase(),
        label: String(s?.label || s?.slug || '').trim() || 'Custom symbol'
      }))
      .filter((o) => o.id && !builtInIds.has(o.id));
    return [...builtIn, ...custom];
  }, [designerSymbols]);
  // Inline editing was replaced with a modal edit panel to keep the table readable.
  const [editingItem] = useState(null); // legacy (kept to minimize churn)
  const [editingModalItemId, setEditingModalItemId] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // Add plant manually modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [addingPlant, setAddingPlant] = useState(false);
  // Add-modal photo picker (for manual/db plants)
  const [addPhotosLoading, setAddPhotosLoading] = useState(false);
  const [addPhotosError, setAddPhotosError] = useState('');
  const [addPhotoOptions, setAddPhotoOptions] = useState([]);
  const [addSelectedPhotoUrl, setAddSelectedPhotoUrl] = useState('');
  // AI lookup -> create private plant -> add to project list
  const [aiLookupQuery, setAiLookupQuery] = useState('');
  const [aiLookupLoading, setAiLookupLoading] = useState(false);
  const [aiLookupError, setAiLookupError] = useState('');
  const [aiDraftPlant, setAiDraftPlant] = useState(null); // draft plant definition for /api/plants/add-custom
  const [aiSmartResults, setAiSmartResults] = useState([]); // for descriptive queries via /smart-search
  const [aiPhotosLoading, setAiPhotosLoading] = useState(false);
  const [aiPhotosError, setAiPhotosError] = useState('');
  const [aiPhotoOptions, setAiPhotoOptions] = useState([]); // [{url, thumbnail, attribution, source, verified, ...}]
  const [aiSelectedPhotoUrl, setAiSelectedPhotoUrl] = useState('');
  const [addingAIPlant, setAddingAIPlant] = useState(false);
  const [newPlantData, setNewPlantData] = useState({
    quantity: 1,
    pot_size: '',
    color: '',
    width: '',
    height: '',
    common_name: '',
    botanical_name: '',
    unit_price: '',
    code: '',
    exposure: '',
    plant_type: '',
    image_url: '',
    quantity_ordered: 0,
    quantity_received: 0,
    quantity_planted: 0,
    status: 'pending',
    notes: '',
    manual_plan_zone_id: ''
  });

  useEffect(() => {
    // Load designer symbol library for dropdown options
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const API_BASE_URL = process.env.REACT_APP_API_URL ||
          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
        const resp = await fetch(`${API_BASE_URL}/designer-symbols`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok) setDesignerSymbols(Array.isArray(data.symbols) ? data.symbols : []);
      } catch {
        // ignore
      }
    })();
  }, []);
  // CSV import state (wizard: file → match columns → review mapped rows → import)
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRawRecords, setCsvRawRecords] = useState([]);
  const [csvIgnoredBlankRows, setCsvIgnoredBlankRows] = useState(0);
  const [csvColumnMapping, setCsvColumnMapping] = useState(() => emptyProjectCsvColumnMapping());
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvIncludedRows, setCsvIncludedRows] = useState({});
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportStatus, setCsvImportStatus] = useState('');
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [csvEnrichingNames, setCsvEnrichingNames] = useState(false);
  const commonNameLookupCacheRef = useRef(new Map());
  // Add from designer master library modal state
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedLibraryItems, setSelectedLibraryItems] = useState(new Set());
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);

  // Inventory check while selecting from designer library
  const [libraryCheckInventory, setLibraryCheckInventory] = useState(false);
  const [libraryAvailability, setLibraryAvailability] = useState({});
  const [libraryAvailabilityLoading, setLibraryAvailabilityLoading] = useState(false);
  const [libraryAvailabilityError, setLibraryAvailabilityError] = useState('');
  const lastLibraryAvailabilitySignatureRef = useRef('');
  // Multi-select state
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Bulk symbol modal
  const [showBulkSymbolModal, setShowBulkSymbolModal] = useState(false);
  const [bulkSymbol, setBulkSymbol] = useState('circle');
  const [bulkSymbolSizeDisplay, setBulkSymbolSizeDisplay] = useState('0.5'); // in or cm depending on project units
  const [bulkApplyingSymbol, setBulkApplyingSymbol] = useState(false);
  const [bulkSymbolScope, setBulkSymbolScope] = useState('selected'); // 'selected' | 'filtered'
  // PDF preview state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');

  // Supplier matching state
  const [supplierAvailability, setSupplierAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showSupplierAvailability, setShowSupplierAvailability] = useState(false);
  const lastSupplierAvailabilitySignatureRef = useRef('');

  // Placed counts (from plan placements)
  const [placedScope, setPlacedScope] = useState('current'); // 'current' | 'all'
  const [placedPlanId, setPlacedPlanId] = useState(null);
  const [planZones, setPlanZones] = useState([]);
  const [planZonesLoading, setPlanZonesLoading] = useState(false);
  const [planZonesError, setPlanZonesError] = useState('');
  const [showZonesPanel, setShowZonesPanel] = useState(true);
  const [newZoneNameInput, setNewZoneNameInput] = useState('');
  const [newZoneSeason, setNewZoneSeason] = useState('spring');
  const [newZoneSunExposure, setNewZoneSunExposure] = useState('');
  const [newZoneSiteNotes, setNewZoneSiteNotes] = useState('');
  const [savingZone, setSavingZone] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [editZoneName, setEditZoneName] = useState('');
  const [editZoneSeason, setEditZoneSeason] = useState('spring');
  const [editZoneSunExposure, setEditZoneSunExposure] = useState('');
  const [editZoneSiteNotes, setEditZoneSiteNotes] = useState('');
  const [bulkManualZone, setBulkManualZone] = useState('');
  const [bulkApplyingZone, setBulkApplyingZone] = useState(false);
  const [manualZoneSavingRowId, setManualZoneSavingRowId] = useState(null);
  const [placementsCurrent, setPlacementsCurrent] = useState(null); // null means not loaded
  const [placementsAll, setPlacementsAll] = useState(null); // null means not loaded
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [placementsError, setPlacementsError] = useState('');

  const reloadPlanZones = useCallback(async () => {
    if (!id) return;
    setPlanZonesLoading(true);
    setPlanZonesError('');
    try {
      const data = await planZonesApi.list(id, { planId: placedPlanId || null });
      setPlanZones(Array.isArray(data.zones) ? data.zones : []);
    } catch (e) {
      setPlanZonesError(e.message || 'Failed to load Garden App zones');
      setPlanZones([]);
    } finally {
      setPlanZonesLoading(false);
    }
  }, [id, placedPlanId]);

  useEffect(() => {
    reloadPlanZones();
  }, [reloadPlanZones]);

  // Filter: show plants missing a selected field (helps fill in incomplete imports)
  const [missingFilter, setMissingFilter] = useState(''); // '', 'common_name', 'botanical_name', 'image_url', 'code'
  // Filter: by plant type
  const [typeFilter, setTypeFilter] = useState(''); // '' | '__missing__' | '<plant_type>'

  const projectUnits = useMemo(() => {
    try {
      const pd = project?.project_data
        ? (typeof project.project_data === 'string' ? JSON.parse(project.project_data) : project.project_data)
        : {};
      const u = (pd?.sheet?.units || '').toString().trim().toLowerCase();
      return u === 'cm' ? 'cm' : 'in';
    } catch {
      return 'in';
    }
  }, [project]);

  const inchesToDisplay = (inches) => {
    const n = Number(inches);
    if (!Number.isFinite(n)) return '';
    return projectUnits === 'cm' ? (n * 2.54) : n;
  };

  const displayToInches = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return projectUnits === 'cm' ? (n / 2.54) : n;
  };

  const availableTypes = useMemo(() => {
    const uniq = new Map(); // norm -> original
    (items || []).forEach((it) => {
      const raw = (it?.plant_type ?? '').toString().trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!uniq.has(key)) uniq.set(key, raw);
    });
    return Array.from(uniq.values()).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const visibleItems = useMemo(() => {
    const norm = (v) => (v == null ? '' : String(v)).trim().toLowerCase();
    const isMissing = (v) => v == null || String(v).trim().length === 0;

    let next = Array.isArray(items) ? items : [];

    if (missingFilter) {
      next = next.filter((it) => {
        switch (missingFilter) {
          case 'common_name':
            return isMissing(it.common_name);
          case 'botanical_name':
            return isMissing(it.botanical_name);
          case 'image_url':
            return isMissing(it.image_url);
          case 'code':
            return isMissing(it.code);
          case 'symbol':
            return isMissing(it.symbol);
          case 'symbol_size_in':
            return it.symbol_size_in == null || String(it.symbol_size_in).trim().length === 0;
          default:
            return true;
        }
      });
    }

    if (typeFilter) {
      next = next.filter((it) => {
        if (typeFilter === '__missing__') return isMissing(it?.plant_type);
        return norm(it?.plant_type) === norm(typeFilter);
      });
    }

    return next;
  }, [items, missingFilter, typeFilter]);

  const visibleNeedingCareReviewIds = useMemo(
    () => visibleItems.filter((it) => it.ai_filled_at && !it.care_reviewed_at).map((it) => it.id),
    [visibleItems]
  );

  const csvImportableCount = useMemo(
    () => csvPreview.filter((row) => row._importable).length,
    [csvPreview]
  );

  const csvSelectedCount = useMemo(
    () => csvPreview.filter((row, idx) => row._importable && csvIncludedRows[idx]).length,
    [csvPreview, csvIncludedRows]
  );

  const csvMissingCommonNameCount = useMemo(
    () => csvPreview.filter((row) => row._importable && !String(row.common_name || '').trim() && String(row.botanical_name || '').trim()).length,
    [csvPreview]
  );

  // Add from supplier inventory modal state
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [suppliers] = useState([]);
  const [suppliersLoading] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierInventory, setSupplierInventory] = useState([]);
  const [supplierInventoryLoading, setSupplierInventoryLoading] = useState(false);
  const [supplierInventorySearch, setSupplierInventorySearch] = useState('');
  const [selectedSupplierInventory, setSelectedSupplierInventory] = useState(() => new Map()); // inventoryId -> qty
  const [addingFromSupplier, setAddingFromSupplier] = useState(false);
  const [supplierAddError, setSupplierAddError] = useState('');

  // Inline edit photo picker state (per-row, shown for current editingItem)
  const [editPhotosLoading, setEditPhotosLoading] = useState(false);
  const [editPhotosError, setEditPhotosError] = useState('');
  const [editPhotoOptions, setEditPhotoOptions] = useState([]);
  const [editSelectedPhotoUrl, setEditSelectedPhotoUrl] = useState('');
  const [editPhotoPickerOpenFor, setEditPhotoPickerOpenFor] = useState(null); // itemId or null

  // AI care/seasonal fill state (designer triggers Gemini lookup that writes to master_plant_list)
  const [aiFillingItemId, setAiFillingItemId] = useState(null);
  const [aiBulkFilling, setAiBulkFilling] = useState(false);
  const [aiBulkProgress, setAiBulkProgress] = useState({ done: 0, total: 0 });
  const [aiFillError, setAiFillError] = useState('');
  const [expandedCareItemId, setExpandedCareItemId] = useState(null);
  const [careReviewingItemId, setCareReviewingItemId] = useState(null);
  const [bulkMarkingReviewed, setBulkMarkingReviewed] = useState(false);

  useEffect(() => {
    fetchMasterList(false); // Don't auto-sync from plan on load
    fetchProject();
    didAutoEditRef.current = false;
  }, [id]);

  // Fetch supplier availability only when user asks for it
  useEffect(() => {
    if (!showSupplierAvailability) return;
    if (items.length === 0) return;
    const sig = items.map((it) => it.id).join('|');
    if (lastSupplierAvailabilitySignatureRef.current === sig) return;
    lastSupplierAvailabilitySignatureRef.current = sig;
    fetchSupplierAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, showSupplierAvailability]);

  // Determine which drawing we should use for "current drawing" placed counts.
  // Prefer ?planId= from URL, then fall back to localStorage activePlanId:<projectId>.
  useEffect(() => {
    let nextPlanId = null;
    try {
      const params = new URLSearchParams(location.search);
      nextPlanId = params.get('planId') || null;
    } catch {
      nextPlanId = null;
    }
    if (!nextPlanId) {
      try {
        nextPlanId = localStorage.getItem(`activePlanId:${id}`) || null;
      } catch {
        nextPlanId = null;
      }
    }
    setPlacedPlanId(nextPlanId);
  }, [id, location.search]);

  // If the selected "current drawing" changes, refresh its cached placements.
  useEffect(() => {
    setPlacementsCurrent(null);
  }, [placedPlanId]);

  // If switching projects, reset placement caches.
  useEffect(() => {
    setPlacementsCurrent(null);
    setPlacementsAll(null);
  }, [id]);

  const fetchPlacementsForScope = async ({ scope, planId }) => {
    const token = localStorage.getItem('authToken');
    const qs = scope === 'current' && planId ? `?planId=${encodeURIComponent(planId)}` : '';
    const url = `/api/plant-placements/project/${id}${scope === 'all' ? '' : qs}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || 'Failed to load placements');
    return Array.isArray(data.placements) ? data.placements : [];
  };

  // Load placements for placed counts on demand, with simple caching.
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      try {
        setPlacementsError('');
        if (placedScope === 'current') {
          if (!placedPlanId) return;
          if (placementsCurrent !== null) return;
          setPlacementsLoading(true);
          const rows = await fetchPlacementsForScope({ scope: 'current', planId: placedPlanId });
          if (!isCancelled) setPlacementsCurrent(rows);
          return;
        }
        if (placedScope === 'all') {
          if (placementsAll !== null) return;
          setPlacementsLoading(true);
          const rows = await fetchPlacementsForScope({ scope: 'all', planId: null });
          if (!isCancelled) setPlacementsAll(rows);
        }
      } catch (e) {
        if (!isCancelled) setPlacementsError(e.message || 'Failed to load placed counts');
      } finally {
        if (!isCancelled) setPlacementsLoading(false);
      }
    };
    run();
    return () => { isCancelled = true; };
  }, [id, placedScope, placedPlanId, placementsCurrent, placementsAll]);

  const placedPlacements = placedScope === 'all' ? placementsAll : placementsCurrent;
  const placedMaps = useMemo(() => {
    const byMaster = {};
    const byPlant = {};
    let hasAnyMasterListId = false;
    for (const p of Array.isArray(placedPlacements) ? placedPlacements : []) {
      const q = Number(p?.quantity) || 1;
      const masterId = p?.master_list_id || null;
      const plantId = p?.plant_id || null;
      if (masterId) {
        hasAnyMasterListId = true;
        byMaster[masterId] = (byMaster[masterId] || 0) + q;
      }
      if (plantId) {
        byPlant[plantId] = (byPlant[plantId] || 0) + q;
      }
    }
    return { byMaster, byPlant, hasAnyMasterListId };
  }, [placedPlacements]);

  // Auto-open inline edit row when navigated from the plan (e.g. ?editItem=<id>)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editItemId = params.get('editItem');
    if (!editItemId) return;
    if (didAutoEditRef.current) return;
    if (!items || items.length === 0) return;

    const target = items.find((it) => String(it.id) === String(editItemId));
    if (!target) return;

    didAutoEditRef.current = true;
    startEditing(target);

    // Scroll row into view for clarity
    setTimeout(() => {
      try {
        const el = document.getElementById(`mpl-row-${target.id}`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch {
        // ignore
      }
    }, 50);

    // Clean URL (remove editItem so refresh doesn't re-trigger)
    try {
      params.delete('editItem');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, location.search]);

  const fetchSupplierAvailability = async () => {
    try {
      setLoadingAvailability(true);
      const token = localStorage.getItem('authToken');
      const plants = items.map(item => ({
        name: item.common_name,
        botanical_name: item.botanical_name
      }));

      const response = await fetch('/api/suppliers/availability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plants })
      });

      if (!response.ok) throw new Error('Failed to fetch availability');
      const data = await response.json();
      setSupplierAvailability(data.availability || {});
    } catch (error) {
      console.error('Error fetching availability:', error);
      alert(`Failed to check availability: ${error.message || error}`);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const fetchInventoryForSupplier = async (supplierId) => {
    if (!supplierId) return;
    try {
      setSupplierInventoryLoading(true);
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/suppliers/${supplierId}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Failed to load supplier inventory');
      const data = await resp.json().catch(() => ({}));
      setSupplierInventory(Array.isArray(data.inventory) ? data.inventory : []);
    } catch (e) {
      console.error('Error loading supplier inventory:', e);
      setSupplierInventory([]);
    } finally {
      setSupplierInventoryLoading(false);
    }
  };

  const toggleSupplierInventorySelection = (inventoryId, qty = 1) => {
    setSelectedSupplierInventory((prev) => {
      const next = new Map(prev);
      if (next.has(inventoryId)) next.delete(inventoryId);
      else next.set(inventoryId, Math.max(1, Number(qty) || 1));
      return next;
    });
  };

  const setSupplierInventoryQty = (inventoryId, qty) => {
    setSelectedSupplierInventory((prev) => {
      const next = new Map(prev);
      if (!next.has(inventoryId)) return next;
      next.set(inventoryId, Math.max(1, Number(qty) || 1));
      return next;
    });
  };

  const bestEffortFindImageUrl = async ({ botanicalName, commonName }) => {
    const b = (botanicalName || '').toString().trim();
    const c = (commonName || '').toString().trim();
    if (!b && !c) return null;
    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const photos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];
      return photos?.[0]?.url || null;
    } catch {
      return null;
    }
  };

  const addSelectedFromSupplierInventory = async () => {
    if (!selectedSupplierId) {
      setSupplierAddError('Please choose a supplier.');
      return;
    }
    if (selectedSupplierInventory.size === 0) return;
    setAddingFromSupplier(true);
    setSupplierAddError('');
    try {
      const token = localStorage.getItem('authToken');
      const supplier = suppliers.find((s) => String(s.id) === String(selectedSupplierId)) || null;

      // Build selected items
      const selected = supplierInventory.filter((inv) => selectedSupplierInventory.has(inv.id));

      // Add items one-by-one (best-effort image lookup per item)
      for (const inv of selected) {
        const qty = selectedSupplierInventory.get(inv.id) || 1;
        const commonName = inv.plant_name || '';
        const botanicalName = inv.botanical_name || '';
        const pot = inv.pot || inv.size || '';
        const code = inv.sku || '';
        const unitPrice = inv.price ?? null;
        const imageUrl = await bestEffortFindImageUrl({ botanicalName, commonName });

        const notesParts = [];
        if (supplier?.name) notesParts.push(`Supplier: ${supplier.name}`);
        if (inv.location) notesParts.push(`Location: ${inv.location}`);
        if (inv.availability) notesParts.push(`Availability: ${inv.availability}`);
        if (code) notesParts.push(`SKU: ${code}`);
        const notes = notesParts.join(' • ');

        const resp = await fetch(`/api/plans/${id}/master-list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            plant_id: null,
            common_name: commonName,
            botanical_name: botanicalName,
            quantity: qty,
            pot_size: pot,
            unit_price: unitPrice,
            code,
            notes,
            image_url: imageUrl,
            detected_text: null,
            detection_confidence: null
          })
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || err.details || 'Failed to add one or more inventory items');
        }
      }

      setShowSupplierModal(false);
      setSelectedSupplierId('');
      setSupplierInventory([]);
      setSelectedSupplierInventory(new Map());
      await fetchMasterList(false);
    } catch (e) {
      console.error('Add from supplier inventory error:', e);
      setSupplierAddError(e.message || 'Failed to add from supplier inventory');
    } finally {
      setAddingFromSupplier(false);
    }
  };

  const getAvailabilityBadge = (item) => {
    const key = item.botanical_name || item.common_name;
    const matches = supplierAvailability[key] || [];
    
    if (matches.length === 0) {
      return <span className="availability-badge none">❌ No matches</span>;
    }

    return (
      <div className="availability-stack">
        {matches.map((m, i) => (
          <span key={i} className="availability-badge match" title={`${m.supplier_name}: ${m.pot || ''} ${m.size || ''} - ${m.availability || 'In Stock'} - $${m.price || '?'}`}>
            ✅ {m.supplier_name} {m.pot ? `(${m.pot})` : ''}
          </span>
        ))}
      </div>
    );
  };

  const exportOrderCSV = () => {
    if (items.length === 0) return;
    if (!showSupplierAvailability || loadingAvailability) {
      alert('Click "Check availability" first to populate supplier matches before exporting an order.');
      return;
    }

    // Group items by supplier
    const supplierOrders = {};
    
    items.forEach(item => {
      const key = item.botanical_name || item.common_name;
      const matches = supplierAvailability[key] || [];
      
      if (matches.length > 0) {
        // For MVP, we'll just put it in the first supplier that has a match
        // In a real app, user would select which supplier to order from
        const bestMatch = matches[0];
        const sName = bestMatch.supplier_name;
        
        if (!supplierOrders[sName]) supplierOrders[sName] = [];
        supplierOrders[sName].push({
          name: item.common_name,
          botanical: item.botanical_name,
          quantity: item.quantity,
          size: item.pot_size,
          supplier_pot: bestMatch.pot,
          supplier_size: bestMatch.size,
          sku: bestMatch.sku,
          price: bestMatch.price
        });
      } else {
        if (!supplierOrders['Unassigned']) supplierOrders['Unassigned'] = [];
        supplierOrders['Unassigned'].push({
          name: item.common_name,
          botanical: item.botanical_name,
          quantity: item.quantity,
          size: item.pot_size,
          sku: '',
          price: ''
        });
      }
    });

    // Build CSV content
    let csvContent = 'Supplier,Plant Name,Botanical Name,Plan Size,Supplier Pot,Supplier Size,Quantity,SKU,Estimated Price\n';
    
    Object.keys(supplierOrders).forEach(supplier => {
      supplierOrders[supplier].forEach(item => {
        csvContent += `"${supplier}","${item.name}","${item.botanical}","${item.size}","${item.supplier_pot || ''}","${item.supplier_size || ''}",${item.quantity},"${item.sku}","${item.price}"\n`;
      });
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `order-${project?.name || 'project'}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Clear selected items that no longer exist in the list
  useEffect(() => {
    const itemIds = new Set(items.map(item => item.id));
    setSelectedItems(prev => {
      const next = new Set();
      prev.forEach(id => {
        if (itemIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [items]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch project');
      const data = await response.json();
      setProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchMasterList = async (syncFromPlan = false) => {
    try {
      const token = localStorage.getItem('authToken');
      const url = syncFromPlan 
        ? `/api/plans/${id}/master-list?sync=true`
        : `/api/plans/${id}/master-list`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch Project Plant List');
      const data = await response.json();
      setItems(data.items || []);
      setLoading(false);
      return data;
    } catch (error) {
      console.error('Error fetching master list:', error);
      setLoading(false);
      throw error;
    }
  };

  /** OK = also sync to personal Designer Master Plant List; Cancel = project list only. */
  const promptBackupToDesignerMasterList = () =>
    window.confirm(
      'Also save a copy to your personal Designer Master Plant List (catalog backup)?\n\n' +
        'OK — recommended: keeps plant identity (and AI care, after you run AI fill) in your catalog if this project is ever deleted.\n' +
        'Cancel — project only: the plant stays only on this project’s list.'
    );

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Serialize AI calls in this tab so rapid row clicks don't stack parallel requests.
  const aiFillQueueRef = useRef(Promise.resolve());

  // Trigger AI (Gemini) lookup for a single master list item and persist to master_plant_list.
  // Retries any 429 (app cooldown or Gemini busy) using Retry-After / retry_after_s.
  const aiFillItem = async (itemId, { silent = false } = {}) => {
    if (!itemId) return null;

    const execute = async () => {
      const MAX_COOLDOWN_ATTEMPTS = 40;
      try {
        if (!silent) {
          setAiFillingItemId(itemId);
          setAiFillError('');
        }
        const token = localStorage.getItem('authToken');

        for (let attempt = 0; attempt < MAX_COOLDOWN_ATTEMPTS; attempt++) {
          const resp = await fetch(`/api/plans/${id}/master-list/${itemId}/ai-fill`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ overwrite: false })
          });

          if (resp.ok) {
            const data = await resp.json().catch(() => ({}));
            const updated = data.item || data.updated || null;
            if (updated) {
              setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...updated } : it)));
            } else {
              await fetchMasterList(false);
            }
            return updated;
          }

          const errBody = await resp.json().catch(() => ({}));
          const code = String(errBody.error || '');
          const retryAfterHeader = resp.headers.get('Retry-After');
          const retryAfterS = Number(
            errBody.retry_after_s != null ? errBody.retry_after_s : retryAfterHeader
          );

          // Daily cap — do not spin.
          if (resp.status === 429 && code === 'daily_limit') {
            throw new Error(
              errBody.message || "You've reached today's AI fill limit. Try again tomorrow."
            );
          }

          // Any other 429: app cooldown, Gemini quota, etc. — wait and retry same plant.
          if (resp.status === 429) {
            const waitMs = Math.min(
              Math.max((Number.isFinite(retryAfterS) ? retryAfterS : 2) * 1000 + 250, 500),
              120000
            );
            await sleep(waitMs);
            continue;
          }

          throw new Error(errBody.message || errBody.error || `AI fill failed (${resp.status})`);
        }

        throw new Error('AI fill kept hitting rate limits. Try again in a minute.');
      } catch (e) {
        console.error('AI fill error:', e);
        const msg = e.message || 'AI fill failed';
        if (!silent) setAiFillError(msg);
        // Bulk (silent) must reject so aiFillMissing can abort; single-row clicks must not reject.
        if (silent && /today's AI fill limit|daily limit/i.test(msg)) throw e;
        return null;
      } finally {
        if (!silent) setAiFillingItemId(null);
      }
    };

    const p = aiFillQueueRef.current.then(() => execute());
    aiFillQueueRef.current = p.catch(() => {});
    return p;
  };

  // Fill all missing rows one request at a time (avoids Vercel timeouts and stays under server cooldown).
  // Progress is shown in the toolbar; safe to leave the tab open while it runs.
  const aiFillMissing = async () => {
    const targets = items.filter((it) => !it.ai_filled_at);
    if (targets.length === 0) {
      setAiFillError('Nothing to fill — every plant already has AI care info.');
      return;
    }
    setAiBulkFilling(true);
    setAiFillError('');
    setAiBulkProgress({ done: 0, total: targets.length });
    let failed = 0;
    try {
      for (let i = 0; i < targets.length; i++) {
        const row = await aiFillItem(targets[i].id, { silent: true });
        if (!row) failed += 1;
        setAiBulkProgress({ done: i + 1, total: targets.length });
        // Small gap between plants so we don't stack requests before the server's min interval.
        if (i < targets.length - 1) {
          await sleep(450);
        }
      }
      await fetchMasterList(false);
      if (failed > 0) {
        setAiFillError(
          `${failed} plant${failed === 1 ? '' : 's'} could not be filled (limits or errors). Refresh and retry failed rows.`
        );
      }
    } catch (e) {
      console.error('Bulk AI fill error:', e);
      setAiFillError(e.message || 'Bulk AI fill stopped.');
      await fetchMasterList(false);
    } finally {
      setAiBulkFilling(false);
    }
  };

  const markCareReviewed = async (itemId) => {
    if (!itemId || careReviewingItemId) return;
    setCareReviewingItemId(itemId);
    setAiFillError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plans/master-list/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ care_reviewed: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to mark care info reviewed');
      }
      await fetchMasterList(false);
    } catch (err) {
      console.error('Care review error:', err);
      setAiFillError(err.message || 'Failed to mark care info reviewed');
    } finally {
      setCareReviewingItemId(null);
    }
  };

  const markBulkCareReviewed = async (itemIds, confirmMessage) => {
    if (!Array.isArray(itemIds) || itemIds.length === 0 || bulkMarkingReviewed) return;
    if (!window.confirm(confirmMessage)) return;
    setBulkMarkingReviewed(true);
    setAiFillError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/plans/master-list/bulk-care-reviewed', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ itemIds })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Bulk mark reviewed failed');
      }
      await fetchMasterList(false);
      const u = data.updatedCount ?? 0;
      const s = data.skippedNeedAiOrMissing ?? 0;
      alert(
        `Marked ${u} plant${u === 1 ? '' : 's'} as reviewed.${
          s > 0 ? ` ${s} skipped (no AI care yet, not yours, or already reviewed).` : ''
        }`
      );
    } catch (err) {
      console.error('Bulk care review error:', err);
      setAiFillError(err.message || 'Bulk mark reviewed failed');
    } finally {
      setBulkMarkingReviewed(false);
    }
  };

  const startEditing = (item) => {
    const careValues = CARE_FIELD_KEYS.reduce((acc, key) => {
      acc[key] = item[key] ?? '';
      return acc;
    }, {});
    setEditingModalItemId(item.id);
    setEditPhotosError('');
    setEditPhotoOptions([]);
    setEditPhotosLoading(false);
    setEditSelectedPhotoUrl(item.image_url || '');
    setEditPhotoPickerOpenFor(null);
    setEditValues({
      common_name: item.common_name || '',
      botanical_name: item.botanical_name || '',
      quantity: item.quantity,
      pot_size: item.pot_size || '',
      color: item.color || '',
      notes: item.notes || '',
      image_url: item.image_url || '',
      // Image edits are project-only by default. Designer can opt-in to also update library image.
      propagate_image_to_library: false,
      overwrite_library_image: false,
      quantity_ordered: item.quantity_ordered || 0,
      quantity_received: item.quantity_received || 0,
      quantity_planted: item.quantity_planted || 0,
      unit_price: item.unit_price ?? '',
      width: item.width ?? '',
      height: item.height ?? '',
      exposure: item.exposure || '',
      plant_type: item.plant_type || '',
      code: item.code || '',
      status: item.status || 'pending',
      ...careValues,
      manual_plan_zone_id: item.manual_plan_zone_id || ''
    });
  };

  const cancelEditing = () => {
    setEditingModalItemId(null);
    setEditValues({});
    setEditPhotosError('');
    setEditPhotoOptions([]);
    setEditPhotosLoading(false);
    setEditSelectedPhotoUrl('');
    setEditPhotoPickerOpenFor(null);
  };

  const fetchEditPhotos = async ({ botanicalName, commonName, itemId }) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;

    setEditPhotosLoading(true);
    setEditPhotosError('');
    setEditPhotoOptions([]);
    setEditPhotoPickerOpenFor(itemId);

    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setEditPhotoOptions(photos);

      if (photos.length > 0) {
        // Auto-select first result if current image is empty
        setEditSelectedPhotoUrl((prev) => prev || photos[0].url);
        setEditValues((prev) => {
          if (!prev) return prev;
          if (prev.image_url && String(prev.image_url).trim()) return prev;
          return { ...prev, image_url: photos[0].url };
        });
      }
    } catch (err) {
      console.error('Error fetching edit photos:', err);
      setEditPhotosError(err.message || 'Failed to find photos');
      setEditPhotoOptions([]);
    } finally {
      setEditPhotosLoading(false);
    }
  };

  const selectEditPhoto = (photo) => {
    if (!photo?.url) return;
    setEditSelectedPhotoUrl(photo.url);
    setEditValues((prev) => ({ ...prev, image_url: photo.url }));
  };

  const saveEditing = async (itemId) => {
    try {
      const payload = {
        ...editValues,
        status: editValues.status || 'pending'
      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plans/master-list/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...payload,
          plan_id: placedPlanId || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || 'Failed to update item');
      }
      
      const data = await response.json();
      if (data.success) {
        setEditingModalItemId(null);
        setEditValues({});
        await fetchMasterList(false); // Refresh without syncing
      } else {
        throw new Error('Update was not successful');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert(`Failed to update item: ${error.message}`);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Remove this plant from the project plant list? This will not remove it from the plan.')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plans/master-list/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete item');
      }
      
      const data = await response.json();
      if (data.success) {
        // Remove from selected items if it was selected
        setSelectedItems(prev => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
        // Refresh the list (without syncing from plan)
        await fetchMasterList(false);
      } else {
        throw new Error('Delete was not successful');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(`Failed to delete item: ${error.message}`);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(new Set(visibleItems.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const deleteSelectedItems = async () => {
    if (selectedItems.size === 0) return;
    
    const count = selectedItems.size;
    if (!window.confirm(`Remove ${count} plant${count > 1 ? 's' : ''} from the project plant list? This will not remove them from the plan.`)) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plans/master-list/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ itemIds: Array.from(selectedItems) })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete items');
      }
      
      const data = await response.json();
      const deletedCount = data.deletedCount || data.deleted_count || 0;
      const failedCount = count - deletedCount;
      
      setSelectedItems(new Set());
      // Refresh the list (without syncing from plan)
      await fetchMasterList(false);
      
      if (failedCount > 0) {
        alert(`Successfully removed ${deletedCount} plant${deletedCount !== 1 ? 's' : ''} from the Project Plant List.\n\n⚠️ ${failedCount} plant${failedCount !== 1 ? 's' : ''} could not be removed.`);
      } else {
        alert(`Successfully removed ${deletedCount} plant${deletedCount !== 1 ? 's' : ''} from the Project Plant List.`);
      }
    } catch (error) {
      console.error('Error deleting items:', error);
      alert(`Failed to delete items: ${error.message}`);
    }
  };

  const bulkApplySymbolToSelected = async () => {
    const ids =
      bulkSymbolScope === 'filtered'
        ? visibleItems.map((it) => it.id)
        : Array.from(selectedItems);
    const count = ids.length;
    if (count === 0) return;
    const labelUnits = projectUnits === 'cm' ? 'cm' : 'in';
    const scopeLabel = bulkSymbolScope === 'filtered' ? 'shown (filtered)' : 'selected';
    if (!window.confirm(`Apply symbol settings to ${count} ${scopeLabel} plant${count > 1 ? 's' : ''}? This will overwrite existing symbol settings.`)) {
      return;
    }

    const sizeIn = displayToInches(bulkSymbolSizeDisplay);
    setBulkApplyingSymbol(true);
    try {
      const token = localStorage.getItem('authToken');
      const debugPlacement = (() => {
        try {
          return localStorage.getItem('debugPlacement') === '1';
        } catch {
          return false;
        }
      })();
      const resp = await fetch(`/api/plans/master-list/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(debugPlacement ? { 'X-Placement-Debug': '1' } : {})
        },
        body: JSON.stringify({
          itemIds: ids,
          symbol: bulkSymbol,
          symbol_size_in: sizeIn
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Bulk update failed');
      await fetchMasterList(false);
      setShowBulkSymbolModal(false);
      alert(`Updated ${data.updatedCount || 0} item(s).`);
    } catch (e) {
      console.error('bulkApplySymbolToSelected error:', e);
      alert(e.message || `Failed to bulk apply symbol (${labelUnits})`);
    } finally {
      setBulkApplyingSymbol(false);
    }
  };

  const patchItemManualZone = async (itemId, zoneIdOrEmpty) => {
    const token = localStorage.getItem('authToken');
    setManualZoneSavingRowId(itemId);
    try {
      const resp = await fetch(`/api/plans/master-list/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          manual_plan_zone_id: zoneIdOrEmpty || '',
          plan_id: placedPlanId || null
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to update zone');
      await fetchMasterList(false);
    } catch (e) {
      alert(e.message || 'Failed to update Garden App zone');
    } finally {
      setManualZoneSavingRowId(null);
    }
  };

  const bulkApplyManualZoneToSelected = async () => {
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Set Garden App zone for ${ids.length} selected plant${ids.length === 1 ? '' : 's'}? This overrides map-based zone detection for those rows.`
      )
    ) {
      return;
    }
    setBulkApplyingZone(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/plans/master-list/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          itemIds: ids,
          manual_plan_zone_id: bulkManualZone || '',
          plan_id: placedPlanId || null
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Bulk zone update failed');
      await fetchMasterList(false);
      setSelectedItems(new Set());
      alert(`Updated Garden App zone on ${data.updatedCount ?? 0} row(s).`);
    } catch (e) {
      alert(e.message || 'Bulk zone update failed');
    } finally {
      setBulkApplyingZone(false);
    }
  };

  const createListOnlyZone = async () => {
    const name = newZoneNameInput.trim();
    if (!name) {
      alert('Enter a zone name.');
      return;
    }
    if (!id) return;
    setSavingZone(true);
    try {
      await planZonesApi.create({
        project_id: id,
        plan_id: placedPlanId || null,
        name,
        season_default: newZoneSeason || 'spring',
        shape_type: 'rect',
        shape: { x: 0, y: 0, width: 0.01, height: 0.01 },
        sun_exposure: newZoneSunExposure.trim() || null,
        site_notes: newZoneSiteNotes.trim() || null,
      });
      setNewZoneNameInput('');
      setNewZoneSunExposure('');
      setNewZoneSiteNotes('');
      await reloadPlanZones();
    } catch (e) {
      alert(e.message || 'Failed to create zone');
    } finally {
      setSavingZone(false);
    }
  };

  const saveZoneEdits = async (zoneId) => {
    const name = editZoneName.trim();
    if (!name) {
      alert('Zone name is required.');
      return;
    }
    setSavingZone(true);
    try {
      await planZonesApi.update(zoneId, {
        name,
        season_default: editZoneSeason || 'spring',
        sun_exposure: editZoneSunExposure.trim() ? editZoneSunExposure : null,
        site_notes: editZoneSiteNotes.trim() ? editZoneSiteNotes.trim() : null,
      });
      setEditingZoneId(null);
      setEditZoneName('');
      setEditZoneSunExposure('');
      setEditZoneSiteNotes('');
      await reloadPlanZones();
    } catch (e) {
      alert(e.message || 'Failed to update zone');
    } finally {
      setSavingZone(false);
    }
  };

  const deletePlanZone = async (zone) => {
    if (!zone?.id) return;
    if (!window.confirm(`Delete zone "${zone.name}"? Plants assigned to it will become unassigned for manual zone (map detection still applies).`)) {
      return;
    }
    setSavingZone(true);
    try {
      await planZonesApi.remove(zone.id);
      if (editingZoneId === zone.id) {
        setEditingZoneId(null);
        setEditZoneName('');
        setEditZoneSunExposure('');
        setEditZoneSiteNotes('');
      }
      await reloadPlanZones();
      await fetchMasterList(false);
    } catch (e) {
      alert(e.message || 'Failed to delete zone');
    } finally {
      setSavingZone(false);
    }
  };

  const searchPlants = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plants?search=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to search plants');
      const data = await response.json();
      setSearchResults(data.plants || data || []);
    } catch (error) {
      console.error('Error searching plants:', error);
      setSearchResults([]);
    }
  };

  const fetchDesignerLibrary = async (query = '') => {
    try {
      setLibraryLoading(true);
      const token = localStorage.getItem('authToken');
      const qs = query && query.trim() ? `?search=${encodeURIComponent(query.trim())}` : '';
      const response = await fetch(`/api/designer-master-plant-list${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load your Master Plant List');
      const data = await response.json();
      setLibraryItems(data.items || []);
    } catch (err) {
      console.error('Error loading designer master plant list:', err);
      setLibraryItems([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  const getLibraryAvailabilityKey = (li) => {
    const b = (li?.botanical_name || '').toString().trim();
    const c = (li?.common_name || '').toString().trim();
    return b || c;
  };

  const fetchLibraryAvailability = async (itemsToCheck) => {
    const list = Array.isArray(itemsToCheck) ? itemsToCheck : [];
    const signature = list.map((x) => String(x?.id || '')).join(',');
    if (!signature) {
      setLibraryAvailability({});
      setLibraryAvailabilityError('');
      return;
    }
    if (lastLibraryAvailabilitySignatureRef.current === signature) return;

    try {
      lastLibraryAvailabilitySignatureRef.current = signature;
      setLibraryAvailabilityLoading(true);
      setLibraryAvailabilityError('');
      const token = localStorage.getItem('authToken');
      const plants = list.map((li) => ({
        name: li.common_name,
        botanical_name: li.botanical_name
      }));

      const response = await fetch('/api/suppliers/availability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plants })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to fetch availability');
      setLibraryAvailability(data.availability || {});
    } catch (e) {
      console.error('Error fetching library availability:', e);
      setLibraryAvailabilityError(e.message || 'Failed to check inventory');
      setLibraryAvailability({});
    } finally {
      setLibraryAvailabilityLoading(false);
    }
  };

  useEffect(() => {
    if (!showLibraryModal) return;
    if (!libraryCheckInventory) return;
    if (libraryLoading) return;
    if (!libraryItems || libraryItems.length === 0) return;
    fetchLibraryAvailability(libraryItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLibraryModal, libraryCheckInventory, libraryLoading, libraryItems]);

  const toggleSelectLibraryItem = (itemId) => {
    setSelectedLibraryItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const addSelectedFromLibrary = async () => {
    if (selectedLibraryItems.size === 0) return;
    setAddingFromLibrary(true);
    try {
      const token = localStorage.getItem('authToken');
      const selected = libraryItems.filter(i => selectedLibraryItems.has(i.id));
      const payloadItems = selected.map(i => ({
        plant_id: i.plant_id || null,
        designer_master_id: i.id,
        common_name: i.common_name,
        botanical_name: i.botanical_name || '',
        quantity: 1,
        pot_size: i.pot_size || '',
        color: i.color || '',
        unit_price: i.unit_price ?? null,
        code: i.code || '',
        width: i.width ?? null,
        height: i.height ?? null,
        exposure: i.exposure || '',
        plant_type: i.plant_type || '',
        notes: i.notes || '',
        status: 'pending'
      }));

      const response = await fetch(`/api/plans/${id}/master-list/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items: payloadItems })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || 'Failed to add plants');
      }

      const data = await response.json();
      const createdCount = data.created_count || 0;
      const updatedCount = data.updated_count || 0;
      const failedCount = data.failed_count || 0;
      if (failedCount > 0) {
        alert(`Added from Master Plant List.\n✅ Created: ${createdCount}\n🟰 Updated: ${updatedCount}\n❌ Failed: ${failedCount}`);
      }

      setShowLibraryModal(false);
      setLibrarySearch('');
      setLibraryItems([]);
      setSelectedLibraryItems(new Set());
      await fetchMasterList(false);
    } catch (err) {
      console.error('Error adding from master plant list:', err);
      alert(`Failed to add plants: ${err.message}`);
    } finally {
      setAddingFromLibrary(false);
    }
  };

  const addPlantManually = async () => {
    // If no plant selected, we need at least a common name
    if (!selectedPlant && !newPlantData.common_name.trim()) {
      alert('Please select a plant or enter a plant name.');
      return;
    }

    const backupToDesignerMaster = promptBackupToDesignerMasterList();

    setAddingPlant(true);
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(`/api/plans/${id}/master-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plant_id: selectedPlant?.id || null,
          common_name: selectedPlant?.common_name || newPlantData.common_name,
          botanical_name: selectedPlant?.botanical_name || newPlantData.botanical_name,
          image_url: selectedPlant?.image_url || newPlantData.image_url || null,
          quantity: newPlantData.quantity,
          pot_size: newPlantData.pot_size,
          color: newPlantData.color,
          unit_price: newPlantData.unit_price ? parseFloat(newPlantData.unit_price) : null,
          width: newPlantData.width ? parseFloat(newPlantData.width) : null,
          height: newPlantData.height ? parseFloat(newPlantData.height) : null,
          code: newPlantData.code,
          exposure: newPlantData.exposure,
          plant_type: newPlantData.plant_type,
          quantity_ordered: newPlantData.quantity_ordered,
          quantity_received: newPlantData.quantity_received,
          quantity_planted: newPlantData.quantity_planted,
          status: newPlantData.status,
          notes: newPlantData.notes,
          plan_id: placedPlanId || null,
          manual_plan_zone_id: (newPlantData.manual_plan_zone_id || '').trim() || null,
          detected_text: null, // Manual addition
          detection_confidence: null,
          backup_to_designer_library: backupToDesignerMaster,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add plant');
      }
      
      // Reset modal state
      setShowAddModal(false);
      setSelectedPlant(null);
      setSearchTerm('');
      setSearchResults([]);
      setAiLookupQuery('');
      setAiLookupError('');
      setAiDraftPlant(null);
      setAddPhotosLoading(false);
      setAddPhotosError('');
      setAddPhotoOptions([]);
      setAddSelectedPhotoUrl('');
      setNewPlantData({
        quantity: 1,
        pot_size: '',
        color: '',
        width: '',
        height: '',
        common_name: '',
        botanical_name: '',
        unit_price: '',
        code: '',
        exposure: '',
        plant_type: '',
        image_url: '',
        quantity_ordered: 0,
        quantity_received: 0,
        quantity_planted: 0,
        status: 'pending',
        notes: '',
        manual_plan_zone_id: ''
      });
      
      // Refresh list
      await fetchMasterList(false);
    } catch (error) {
      console.error('Error adding plant:', error);
      alert(`Failed to add plant: ${error.message}`);
    } finally {
      setAddingPlant(false);
    }
  };

  const fetchAddPhotos = async (botanicalName, commonName) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;

    setAddPhotosLoading(true);
    setAddPhotosError('');
    setAddPhotoOptions([]);

    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setAddPhotoOptions(photos);
      if (photos.length > 0) {
        setAddSelectedPhotoUrl(photos[0].url);
        setNewPlantData((prev) => ({ ...prev, image_url: prev.image_url || photos[0].url }));
      }
    } catch (err) {
      console.error('Error fetching add photos:', err);
      setAddPhotosError(err.message || 'Failed to find photos');
      setAddPhotoOptions([]);
    } finally {
      setAddPhotosLoading(false);
    }
  };

  const selectAddPhoto = (photo) => {
    if (!photo?.url) return;
    setAddSelectedPhotoUrl(photo.url);
    setNewPlantData((prev) => ({ ...prev, image_url: photo.url }));
  };

  const runAiLookup = async () => {
    const q = (aiLookupQuery || '').trim();
    if (!q) {
      setAiLookupError('Please enter a plant name or description.');
      return;
    }

    setAiLookupLoading(true);
    setAiLookupError('');
    setAiDraftPlant(null);
    setAiSmartResults([]);
    setAiPhotosError('');
    setAiPhotoOptions([]);
    setAiSelectedPhotoUrl('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/plants/ai-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ query: q })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // If this is a descriptive query, automatically fall back to smart-search (returns multiple plants + images)
        const msg = (data.error || data.message || data.details || '').toString();
        const looksDescriptive = msg.toLowerCase().includes('descriptive') || msg.toLowerCase().includes('smart-search');
        if (looksDescriptive) {
          const smartResp = await fetch('/api/plants/smart-search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: q })
          });
          const smartData = await smartResp.json().catch(() => ({}));
          if (!smartResp.ok) {
            throw new Error(smartData.error || smartData.message || 'Smart search failed');
          }
          const plants = smartData?.results?.plants || [];
          if (!Array.isArray(plants) || plants.length === 0) {
            throw new Error('No AI results found. Try a different search.');
          }
          setAiSmartResults(plants);
          return;
        }

        throw new Error(data.error || data.details || 'AI lookup failed');
      }

      const plant = data.plant || null;
      if (!plant?.common_name || !plant?.botanical_name) {
        throw new Error('AI lookup returned incomplete plant data.');
      }

      const draft = {
        common_name: plant.common_name || '',
        botanical_name: plant.botanical_name || '',
        category: plant.category || 'Other',
        mature_size: plant.mature_size || '',
        light_requirements: plant.light_requirements || '',
        water_requirements: plant.water_requirements || '',
        hardiness_zones: plant.hardiness_zones || '',
        typical_price: plant.typical_price || '',
        care_notes: plant.care_notes || '',
        growing_notes: plant.growing_notes || '',
        image_suggestion: plant.image_suggestion || '',
        image_url: plant.image_url || '',
        photo_source: plant.photo_source || null,
        photo_attribution: plant.photo_attribution || null,
        image_verified: plant.image_verified || false
      };

      setAiDraftPlant(draft);

      // Pull recommended photos so the designer can pick one (no URL copy/paste)
      if (draft.botanical_name || draft.common_name) {
        await fetchAiPhotos(draft.botanical_name, draft.common_name);
      }
    } catch (err) {
      console.error('AI lookup error:', err);
      setAiLookupError(err.message || 'AI lookup failed');
    } finally {
      setAiLookupLoading(false);
    }
  };

  const fetchAiPhotos = async (botanicalName, commonName) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;

    setAiPhotosLoading(true);
    setAiPhotosError('');
    setAiPhotoOptions([]);

    try {
      // Try verified-only first; if empty, fall back to broader photo sources.
      const payloadBase = {
        botanicalName: b || undefined,
        commonName: c || undefined,
        verifiedOnly: true
      };

      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadBase)
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setAiPhotoOptions(photos);

      // Auto-select first photo if draft doesn't already have one
      if (photos.length > 0) {
        setAiSelectedPhotoUrl((prev) => prev || photos[0].url);
        setAiDraftPlant((prev) => {
          if (!prev) return prev;
          if (prev.image_url && prev.image_url.trim()) return prev;
          const p0 = photos[0];
          return {
            ...prev,
            image_url: p0.url,
            photo_source: p0.source || prev.photo_source || null,
            photo_attribution: p0.attribution || prev.photo_attribution || null,
            image_verified: !!p0.verified
          };
        });
      }
    } catch (err) {
      console.error('Error fetching AI photos:', err);
      setAiPhotosError(err.message || 'Failed to find photos');
      setAiPhotoOptions([]);
    } finally {
      setAiPhotosLoading(false);
    }
  };

  const selectAiPhoto = (photo) => {
    if (!photo?.url) return;
    setAiSelectedPhotoUrl(photo.url);
    setAiDraftPlant((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        image_url: photo.url,
        photo_source: photo.source || prev.photo_source || null,
        photo_attribution: photo.attribution || prev.photo_attribution || null,
        image_verified: !!photo.verified
      };
    });
  };

  const openGoogleImages = (query) => {
    const q = (query || '').trim();
    if (!q) return;
    const url = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const addAiPlantToProject = async () => {
    if (!aiDraftPlant) return;

    const imageUrl = (aiDraftPlant.image_url || '').trim();
    if (!imageUrl) {
      alert('Please choose a recommended photo (or use placeholder) before adding.');
      return;
    }

    const backupToDesignerMaster = promptBackupToDesignerMasterList();

    setAddingAIPlant(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Please sign in again.');

      // 1) Create (or reuse) a designer-private plant record in `plants`
      let plantId = null;
      try {
        const resp = await fetch('/api/plants/add-custom', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...aiDraftPlant,
            image_url: imageUrl,
            photo_source: aiDraftPlant.photo_source || null,
            photo_attribution: aiDraftPlant.photo_attribution || null,
            image_verified: aiDraftPlant.image_verified || false
          })
        });

        const data = await resp.json().catch(() => ({}));
        if (resp.status === 409 && data.plantId) {
          plantId = data.plantId;
        } else if (!resp.ok) {
          throw new Error(data.error || data.details || 'Failed to create plant');
        } else {
          plantId = data.plant?.id || null;
        }
      } catch (e) {
        throw e;
      }

      if (!plantId) throw new Error('Failed to determine created plant id.');

      // 2) Add to this project’s plant list
      const addResp = await fetch(`/api/plans/${id}/master-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plant_id: plantId,
          common_name: aiDraftPlant.common_name,
          botanical_name: aiDraftPlant.botanical_name,
          quantity: newPlantData.quantity || 1,
          pot_size: newPlantData.pot_size || '',
          color: newPlantData.color || '',
          unit_price: newPlantData.unit_price ? parseFloat(newPlantData.unit_price) : null,
          width: newPlantData.width ? parseFloat(newPlantData.width) : null,
          height: newPlantData.height ? parseFloat(newPlantData.height) : null,
          code: newPlantData.code || '',
          exposure: newPlantData.exposure || '',
          plant_type: newPlantData.plant_type || '',
          quantity_ordered: newPlantData.quantity_ordered || 0,
          quantity_received: newPlantData.quantity_received || 0,
          quantity_planted: newPlantData.quantity_planted || 0,
          status: newPlantData.status || 'pending',
          notes: newPlantData.notes || '',
          plan_id: placedPlanId || null,
          manual_plan_zone_id: (newPlantData.manual_plan_zone_id || '').trim() || null,
          detected_text: null,
          detection_confidence: null,
          backup_to_designer_library: backupToDesignerMaster,
        })
      });

      if (!addResp.ok) {
        const errorData = await addResp.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || 'Failed to add plant to project list');
      }

      // Reset and refresh
      setShowAddModal(false);
      setSelectedPlant(null);
      setSearchTerm('');
      setSearchResults([]);
      setAiLookupQuery('');
      setAiLookupError('');
      setAiDraftPlant(null);
      setAiSmartResults([]);
      setAiPhotosError('');
      setAiPhotoOptions([]);
      setAiSelectedPhotoUrl('');
      setNewPlantData({
        quantity: 1,
        pot_size: '',
        color: '',
        width: '',
        height: '',
        common_name: '',
        botanical_name: '',
        unit_price: '',
        code: '',
        exposure: '',
        plant_type: '',
        image_url: '',
        quantity_ordered: 0,
        quantity_received: 0,
        quantity_planted: 0,
        status: 'pending',
        notes: '',
        manual_plan_zone_id: ''
      });

      await fetchMasterList(false);
    } catch (err) {
      console.error('Error adding AI plant:', err);
      alert(`Failed to add AI plant: ${err.message}`);
    } finally {
      setAddingAIPlant(false);
    }
  };

  const parseCsvFile = (file) => {
    setCsvParsing(true);
    setCsvPreview([]);
    setCsvIncludedRows({});
    setCsvImportStatus('');
    setCsvImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = String(e.target.result || '');
        const records = parse(text, {
          columns: true,
          skip_empty_lines: true,
          trim: true,
          relax_column_count: true,
          relax_quotes: true
        });
        const nonBlankRecords = Array.isArray(records)
          ? records.filter((record) =>
              Object.values(record || {}).some((value) => String(value ?? '').trim().length > 0)
            )
          : [];
        if (nonBlankRecords.length === 0) {
          alert('This file looks empty. Use a file with a header row and at least one plant line.');
          setCsvRawRecords([]);
          setCsvHeaders([]);
          setCsvIgnoredBlankRows(0);
          setCsvParsing(false);
          return;
        }
        const headers = Object.keys(nonBlankRecords[0]);
        setCsvHeaders(headers);
        setCsvRawRecords(nonBlankRecords);
        setCsvIgnoredBlankRows(Math.max(0, (Array.isArray(records) ? records.length : 0) - nonBlankRecords.length));
        setCsvColumnMapping(suggestProjectCsvColumnMapping(headers));
      } catch (err) {
        console.error('CSV parse error', err);
        alert(
          `We could not read this file. Save it as CSV from Excel or Google Sheets and try again. (${err.message || err})`
        );
        setCsvRawRecords([]);
        setCsvHeaders([]);
        setCsvIgnoredBlankRows(0);
      } finally {
        setCsvParsing(false);
      }
    };
    reader.onerror = () => {
      setCsvParsing(false);
      alert('Could not read the file.');
    };
    reader.readAsText(file);
  };

  const csvCell = (obj, ...keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return '';
  };

  const csvRowHasPlantData = (row) => [
    row.common_name,
    row.botanical_name,
    row.code,
    row.image_url,
    row.notes
  ].some((v) => v != null && String(v).trim().length > 0);

  const lookupCommonNameByBotanical = async (botanicalName) => {
    const q = String(botanicalName || '').trim();
    if (!q) return '';
    const key = q.toLowerCase();
    if (commonNameLookupCacheRef.current.has(key)) {
      return commonNameLookupCacheRef.current.get(key);
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/plants?search=${encodeURIComponent(q)}&limit=10`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Plant lookup failed');
      const data = await response.json().catch(() => ({}));
      const plants = Array.isArray(data.plants) ? data.plants : Array.isArray(data) ? data : [];
      const exact = plants.find((plant) =>
        String(plant?.botanical_name || '').trim().toLowerCase() === key &&
        String(plant?.common_name || '').trim()
      );
      const common = exact ? String(exact.common_name || '').trim() : '';
      commonNameLookupCacheRef.current.set(key, common);
      return common;
    } catch (err) {
      console.warn('CSV botanical common-name lookup failed:', err);
      commonNameLookupCacheRef.current.set(key, '');
      return '';
    }
  };

  const lookupAiCommonNameByBotanical = async (botanicalName) => {
    const q = String(botanicalName || '').trim();
    if (!q) return '';
    const key = `ai:${q.toLowerCase()}`;
    if (commonNameLookupCacheRef.current.has(key)) {
      return commonNameLookupCacheRef.current.get(key);
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/plants/ai-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ query: q })
      });
      if (!response.ok) throw new Error('AI common-name lookup failed');
      const data = await response.json().catch(() => ({}));
      const common = String(data?.plant?.common_name || '').trim();
      commonNameLookupCacheRef.current.set(key, common);
      return common;
    } catch (err) {
      console.warn('CSV AI common-name lookup failed:', err);
      commonNameLookupCacheRef.current.set(key, '');
      return '';
    }
  };

  const enrichCsvMissingCommonNames = async () => {
    if (csvEnrichingNames || csvMissingCommonNameCount === 0) return;
    setCsvEnrichingNames(true);
    setCsvImportStatus('Finding missing common names with AI…');
    try {
      const nextRows = [...csvPreview];
      for (let idx = 0; idx < nextRows.length; idx += 1) {
        const row = nextRows[idx];
        if (!row?._importable || String(row.common_name || '').trim() || !String(row.botanical_name || '').trim()) continue;
        setCsvImportStatus(`Finding missing common names… ${idx + 1} of ${nextRows.length}`);
        const common = await lookupAiCommonNameByBotanical(row.botanical_name);
        if (common) {
          nextRows[idx] = {
            ...row,
            common_name: common,
            _commonNameSource: 'AI suggestion'
          };
          setCsvPreview([...nextRows]);
        }
      }
    } finally {
      setCsvEnrichingNames(false);
      setCsvImportStatus('');
    }
  };

  const buildCsvPreview = async () => {
    if (!csvRawRecords.length) {
      alert('Pick a file first.');
      return;
    }
    setCsvParsing(true);
    setCsvImportStatus('Checking mapped rows…');

    const toInt = (v, fallback = 0) => {
      if (v === null || v === undefined) return fallback;
      const s = String(v).trim();
      if (!s) return fallback;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? fallback : n;
    };

    const normalizeStatus = (v) => {
      const s = String(v || '').trim().toLowerCase();
      if (!s) return '';
      const map = {
        pending: 'pending',
        quote: 'quoted',
        quoted: 'quoted',
        ordered: 'ordered',
        order: 'ordered',
        received: 'received',
        complete: 'complete',
        completed: 'complete',
        done: 'complete'
      };
      return map[s] || (['pending', 'quoted', 'ordered', 'received', 'complete'].includes(s) ? s : '');
    };

    try {
      const rows = [];
      for (let idx = 0; idx < csvRawRecords.length; idx += 1) {
        const raw = csvRawRecords[idx];
        const row = buildMasterListCsvRow(raw, csvColumnMapping);
        let commonName = row.common_name;
        let commonNameSource = commonName ? 'CSV' : '';
        if (!commonName && row.botanical_name) {
          const lookedUpCommon = await lookupCommonNameByBotanical(row.botanical_name);
          if (lookedUpCommon) {
            commonName = lookedUpCommon;
            commonNameSource = 'Plantaday match';
          }
        }
        const withCounts = {
          ...row,
          common_name: commonName,
          quantity_ordered: toInt(csvCell(raw, 'quantity_ordered', 'ordered', 'qty ordered'), 0),
          quantity_received: toInt(csvCell(raw, 'quantity_received', 'received', 'qty received'), 0),
          quantity_planted: toInt(csvCell(raw, 'quantity_planted', 'planted', 'qty planted'), 0),
          status: normalizeStatus(csvCell(raw, 'status')),
          _rowNumber: idx + 1,
          _commonNameSource: commonNameSource
        };
        const importable = csvRowHasPlantData(withCounts);
        const zoneHeader = csvColumnMapping.garden_app_zone;
        let csvManualZoneId = null;
        let csvZoneWarning = '';
        if (zoneHeader) {
          const cell = String(raw[zoneHeader] ?? '').trim();
          if (cell) {
            const byId = planZones.find((z) => z.id === cell);
            if (byId) {
              csvManualZoneId = byId.id;
            } else {
              const lower = cell.toLowerCase();
              const named = planZones.filter((z) => (z.name || '').trim().toLowerCase() === lower);
              if (named.length === 1) csvManualZoneId = named[0].id;
              else if (named.length > 1) csvZoneWarning = `Multiple zones match "${cell}"`;
              else csvZoneWarning = `Unknown Garden App zone "${cell}"`;
            }
          }
        }
        const reviewParts = [];
        if (!importable) reviewParts.push('No plant name, code, notes, or photo link mapped.');
        if (csvZoneWarning) reviewParts.push(csvZoneWarning);
        rows.push({
          ...withCounts,
          _csv_manual_zone_id: csvManualZoneId,
          _csv_zone_warning: csvZoneWarning,
          _importable: importable,
          _reviewNote: reviewParts.join(' ')
        });
      }

      const included = {};
      rows.forEach((row, idx) => {
        if (row._importable) included[idx] = true;
      });
      setCsvPreview(rows);
      setCsvIncludedRows(included);
      setCsvImportResult(null);
    } finally {
      setCsvParsing(false);
      setCsvImportStatus('');
    }
  };

  const importCsvToMasterList = async () => {
    if (csvImporting) return;
    if (!csvPreview.length) {
      if (csvRawRecords.length > 0) {
        alert('Preview your mapped rows first, then import.');
      }
      return;
    }
    const token = localStorage.getItem('authToken');
    const csvBackupToDesigner = window.confirm(
      'While importing this CSV, also save copies to your personal Designer Master Plant List (catalog backup)?\n\n' +
        'OK — recommended for the whole import.\n' +
        'Cancel — add rows to this project only (no catalog backup for this import).'
    );
    const results = { created: 0, updated: 0, failed: 0, skipped_empty: 0, skippedRows: [], zoneWarnings: [] };
    setCsvImporting(true);
    setCsvImportStatus('Preparing your plant upload…');
    
    const toInt = (v, fallback = 0) => {
      if (v === null || v === undefined) return fallback;
      const s = String(v).trim();
      if (!s) return fallback;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? fallback : n;
    };

    const toNumOrNull = (v) => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      if (!s) return null;
      const cleaned = s.replace(/[$,]/g, '');
      const n = parseFloat(cleaned);
      return Number.isNaN(n) ? null : n;
    };

    const normalizeStatus = (v) => {
      const s = String(v || '').trim().toLowerCase();
      if (!s) return null;
      const map = {
        pending: 'pending',
        quote: 'quoted',
        quoted: 'quoted',
        ordered: 'ordered',
        order: 'ordered',
        received: 'received',
        complete: 'complete',
        completed: 'complete',
        done: 'complete'
      };
      const out = map[s] || (['pending', 'quoted', 'ordered', 'received', 'complete'].includes(s) ? s : null);
      return out || null;
    };

    const get = (obj, ...keys) => {
      for (const k of keys) {
        if (obj && obj[k] !== undefined) return obj[k];
      }
      return undefined;
    };

    try {
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
      const createRows = [];

      for (let idx = 0; idx < csvPreview.length; idx += 1) {
        const row = csvPreview[idx];
        if (!csvIncludedRows[idx] || !row._importable) {
          results.skipped_empty++;
          results.skippedRows.push({
            rowNumber: row?._rowNumber || idx + 1,
            reason: row?._importable ? 'Unchecked before import' : (row?._reviewNote || 'No importable plant data'),
            label: row?.common_name || row?.botanical_name || row?.code || ''
          });
          continue;
        }
        const commonFromCsv = (get(row, 'common_name', 'name', 'plant name') || '').toString();
        const botanicalFromCsv = (get(row, 'botanical_name', 'botanical', 'plant') || '').toString();
        const imageUrl = (get(row, 'image_url') || '').toString().trim() || null;

        // Allow partial imports; only skip fully empty rows.
        const hasAny = [
          commonFromCsv,
          botanicalFromCsv,
          get(row, 'quantity', 'qty', '#'),
          get(row, 'pot_size', 'size'),
          get(row, 'color', 'colour'),
          get(row, 'unit_price', 'price'),
          get(row, 'code', 'sku'),
          get(row, 'width', 'w'),
          get(row, 'height', 'h'),
          get(row, 'exposure', 'expo'),
          get(row, 'plant_type', 'type'),
          get(row, 'notes', 'note'),
          imageUrl
        ].some((v) => v != null && String(v).trim().length > 0);

        if (!hasAny) {
          results.skipped_empty++;
          results.skippedRows.push({
            rowNumber: row?._rowNumber || idx + 1,
            reason: 'No importable plant data',
            label: row?.common_name || row?.botanical_name || row?.code || ''
          });
          continue;
        }
        
        const quantity = toInt(row.quantity ?? row.qty ?? row['#'] ?? 1, 1) || 1;
        const pot_size = (row.pot_size || row.size || '').toString();
        const notes = (row.notes || '').toString();

        if (row._csv_zone_warning) {
          results.zoneWarnings.push({
            rowNumber: row._rowNumber || idx + 1,
            label: commonFromCsv || botanicalFromCsv || (row.code || '').toString(),
            message: row._csv_zone_warning
          });
        }

        const payload = {
          plant_id: null,
          designer_master_id: null,
          common_name: commonFromCsv,
          botanical_name: botanicalFromCsv || null,
          image_url: imageUrl,
          preserve_blank_common_name: true,
          import_row_number: row?._rowNumber || idx + 1,
          import_label: commonFromCsv || botanicalFromCsv || (row.code || '').toString(),
          quantity,
          pot_size,
          color: (row.color || '').toString(),
          unit_price: toNumOrNull(row.unit_price),
          code: (row.code || '').toString(),
          width: toNumOrNull(row.width),
          height: toNumOrNull(row.height),
          exposure: (row.exposure || '').toString(),
          plant_type: (row.plant_type || '').toString(),
          quantity_ordered: toInt(row.quantity_ordered, 0),
          quantity_received: toInt(row.quantity_received, 0),
          quantity_planted: toInt(row.quantity_planted, 0),
          status: normalizeStatus(row.status) || undefined,
          notes,
          plan_id: placedPlanId || null,
          manual_plan_zone_id: row._csv_manual_zone_id || null,
          detected_text: null,
          detection_confidence: null
        };

        createRows.push(payload);
      }

      if (createRows.length > 0) {
        setCsvImportStatus(`Uploading ${createRows.length} plants to your project…`);
        await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
        try {
          const resp = await fetch(`/api/plans/${id}/master-list/bulk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ items: createRows, backup_to_designer_library: csvBackupToDesigner })
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) throw new Error(data.error || 'Bulk import failed');
          results.created += Number(data.created_count || 0);
          results.updated += Number(data.updated_count || 0);
          results.failed += Number(data.failed_count || 0);
          if (Array.isArray(data.failed)) {
            data.failed.forEach((failure) => {
              results.skippedRows.push({
                rowNumber: failure?.input?.import_row_number || '?',
                reason: failure?.error || 'Server could not import this row',
                label: failure?.input?.import_label || ''
              });
            });
          }
        } catch (err) {
          console.error('CSV bulk import error:', err);
          results.failed += createRows.length;
          createRows.forEach((row) => {
            results.skippedRows.push({
              rowNumber: row.import_row_number || '?',
              reason: err.message || 'Bulk import failed',
              label: row.import_label || ''
            });
          });
        }
      }

      setCsvImportResult({ ...results });
      setShowCsvModal(false);
      setCsvFile(null);
      setCsvRawRecords([]);
      setCsvHeaders([]);
      setCsvIgnoredBlankRows(0);
      setCsvColumnMapping(emptyProjectCsvColumnMapping());
      setCsvPreview([]);
      setCsvIncludedRows({});
      fetchMasterList(false);
    } finally {
      setCsvImporting(false);
      setCsvImportStatus('');
    }
  };

  const exportToCSV = () => {
    if (items.length === 0) {
      alert('No plants to export!');
      return;
    }
    
    // CSV header with all fields
    const headers = [
      'ID',
      'Plant ID',
      'Name',
      'Botanical Name',
      'Quantity',
      'Unit Price',
      'Width (ft)',
      'Height (ft)',
      'Exposure',
      'Plant Type',
      'Code',
      'Pot Size',
      'Notes',
      'Ordered',
      'Received',
      'Planted',
      'Image URL'
    ];
    
    // Helper to escape CSV values
    const escapeCsv = (value) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    // Build CSV rows
    const rows = items.map(item => [
      escapeCsv(item.id),
      escapeCsv(item.plant_id || ''),
      escapeCsv(item.common_name),
      escapeCsv(item.botanical_name),
      item.quantity || 0,
      item.unit_price || '',
      item.width || '',
      item.height || '',
      escapeCsv(item.exposure || ''),
      escapeCsv(item.plant_type || ''),
      escapeCsv(item.code || ''),
      escapeCsv(item.pot_size || ''),
      escapeCsv(item.notes || ''),
      item.quantity_ordered || 0,
      item.quantity_received || 0,
      item.quantity_planted || 0,
      escapeCsv(item.image_url || '')
    ].join(','));
    
    // Combine header and rows
    const csvContent = [headers.join(','), ...rows].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${project?.name || 'master-list'}-plants.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getTotalPlants = () => items.length; // Count of unique plant entries
  const getTotalQuantity = () => items.reduce((sum, item) => sum + (item.quantity || 0), 0); // Sum of all quantities
  const getTotalOrdered = () => items.reduce((sum, item) => sum + (item.quantity_ordered || 0), 0);
  const getTotalReceived = () => items.reduce((sum, item) => sum + (item.quantity_received || 0), 0);
  const getTotalPlanted = () => items.reduce((sum, item) => sum + (item.quantity_planted || 0), 0);

  // Generate PDF plant list for printing
  const generatePlantListPDF = async () => {
    try {
      if (items.length === 0) {
        alert('No plants on this Project Plant List yet. Add plants here first, then try the PDF again.');
        return;
      }

      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      // Get designer name and business name
      let designerName = 'Designer';
      let businessName = '';
      try {
        const designerResponse = await fetch(`${API_BASE_URL}/designers/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (designerResponse.ok) {
          const designerData = await designerResponse.json();
          if (designerData.profile) {
            businessName = designerData.profile.businessName || designerData.profile.business_name || '';
            designerName = designerData.profile.displayName || 
                          designerData.profile.full_name || 
                          'Designer';
          }
        }
      } catch (err) {
        console.error('Error fetching designer name:', err);
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      
      let yPos = margin;
      const lineHeight = 8;
      const imageSize = 30; // 30mm for plant images
      const spacing = 5;

      // Title - Project Name
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(project?.name || 'Plant List', margin, yPos);
      yPos += lineHeight * 1.5;

      // Business Name (if available)
      if (businessName) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(businessName, margin, yPos);
        yPos += lineHeight * 1.5;
      }

      // Designer Name
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Designer: ${designerName}`, margin, yPos);
      yPos += lineHeight * 1.5;

      // Date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
      yPos += lineHeight * 2;

      // Get plant images from items
      const plantList = await Promise.all(items.map(async (item) => {
        let imageUrl = item.image_url || null;
        
        // If no image in item, fetch from plant database
        if (!imageUrl && item.plant_id) {
          try {
            const plantResponse = await fetch(`${API_BASE_URL}/plants/${item.plant_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (plantResponse.ok) {
              const plantData = await plantResponse.json();
              imageUrl = plantData.image_url || null;
            }
          } catch (err) {
            console.error('Error fetching plant image:', err);
          }
        }
        
        return {
          ...item,
          image_url: imageUrl,
          pot_size: item.pot_size || '',
          notes: item.notes || ''
        };
      }));
      let currentPage = 1;

      for (let i = 0; i < plantList.length; i++) {
        const plant = plantList[i];
        
        // Check if we need a new page
        if (yPos + imageSize + lineHeight * 4 > pageHeight - margin) {
          pdf.addPage();
          currentPage++;
          yPos = margin;
        }

        // Load and add plant image (skip invalid URLs to avoid console noise)
        const safePlantImgUrl = coerceImageUrl(plant.image_url);
        if (safePlantImgUrl) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise((resolve, reject) => {
              img.onload = () => {
                try {
                  // Convert image to base64
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0);
                  
                  const imgData = canvas.toDataURL('image/jpeg', 0.8);
                  
                  // Add image to PDF (30mm x 30mm)
                  pdf.addImage(imgData, 'JPEG', margin, yPos, imageSize, imageSize);
                  resolve();
                } catch (error) {
                  console.error('Error processing image:', error);
                  resolve(); // Continue without image
                }
              };
              img.onerror = () => {
                console.error('Error loading image:', safePlantImgUrl);
                resolve(); // Continue without image
              };
              img.src = safePlantImgUrl;
            });
          } catch (error) {
            console.error('Error adding image to PDF:', error);
          }
        }

        // Plant information (to the right of image)
        const textX = margin + imageSize + spacing;
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        
        // Add clickable link for plant name
        const plantUrl = `https://plantaday.com/plants?plantId=${plant.plant_id || ''}`;
        const plantNameWidth = pdf.getTextWidth(plant.common_name);
        pdf.text(plant.common_name, textX, yPos + lineHeight);
        pdf.link(textX, yPos + lineHeight - 4, plantNameWidth, lineHeight, { url: plantUrl });
        
        if (plant.botanical_name) {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'italic');
          const botanicalWidth = pdf.getTextWidth(plant.botanical_name);
          pdf.text(plant.botanical_name, textX, yPos + lineHeight * 2);
          pdf.link(textX, yPos + lineHeight * 2 - 4, botanicalWidth, lineHeight, { url: plantUrl });
        }

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Quantity: ${plant.quantity || 1}`, textX, yPos + lineHeight * 3);
        
        if (plant.pot_size) {
          pdf.text(`Pot Size: ${plant.pot_size}`, textX, yPos + lineHeight * 4);
        }

        // Move to next plant position
        yPos += Math.max(imageSize, lineHeight * 4) + spacing * 2;

        // Add a subtle line separator
        if (i < plantList.length - 1) {
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.line(margin, yPos - spacing, pageWidth - margin, yPos - spacing);
        }
      }

      // Generate PDF blob and show preview
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const fileName = `${project?.name || 'plant-list'}-${new Date().toISOString().split('T')[0]}.pdf`;
      
      setPdfBlobUrl(url);
      setPdfFileName(fileName);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const downloadPDF = () => {
    if (pdfBlobUrl && pdfFileName) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = pdfFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const closePdfPreview = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
    setShowPdfPreview(false);
    setPdfBlobUrl(null);
    setPdfFileName('');
  };

  const getStatusBadge = (item) => {
    // If status is manually set, use it; otherwise calculate from quantities
    if (item.status && item.status !== 'pending') {
      const statusMap = {
        'quoted': '💬 Quoted',
        'ordered': '🛒 Ordered',
        'received': '📦 Received',
        'complete': '✅ Complete'
      };
      const statusText = statusMap[item.status] || item.status;
      return <span className={`status-badge ${item.status}`}>{statusText}</span>;
    }
    
    // Auto-calculate status from quantities if not manually set
    const total = item.quantity;
    const planted = item.quantity_planted || 0;
    const received = item.quantity_received || 0;
    const ordered = item.quantity_ordered || 0;

    if (planted >= total) return <span className="status-badge complete">✅ Complete</span>;
    if (received > 0) return <span className="status-badge received">📦 Received ({received})</span>;
    if (ordered > 0) return <span className="status-badge ordered">🛒 Ordered ({ordered})</span>;
    return <span className="status-badge pending">⏳ Pending</span>;
  };

  if (loading) {
    return (
      <div className="master-plant-list">
        <div className="loading">Loading Project Plant List…</div>
      </div>
    );
  }

  const editingModalItem = editingModalItemId
    ? (items.find((it) => String(it.id) === String(editingModalItemId)) || null)
    : null;

  return (
    <div className="master-plant-list">
      <div className="master-list-header">
        <div className="header-top">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate(`/plan/${id}`)}>
              ← Back to Project
            </button>
            <h1>📋 Project Plant List</h1>
            {project && <p className="project-name">{project.name}</p>}
            <p className="text-sm text-gray-600 mt-2 max-w-3xl" style={{ fontWeight: 400 }}>
              Plants for <strong>this project only</strong>. Build the list from plan recognition, manual add, import, or by adding entries from your{' '}
              <strong>Master Plant List</strong> (your catalog across all projects).
            </p>
          </div>
          <div className="header-stats">
            <div className="stat">
              <span className="stat-label">List Items</span>
              <span className="stat-value">{items.length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Plants</span>
              <span className="stat-value">{getTotalPlants()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Ordered</span>
              <span className="stat-value">{getTotalOrdered()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Received</span>
              <span className="stat-value">{getTotalReceived()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Planted</span>
              <span className="stat-value">{getTotalPlanted()}</span>
            </div>
          </div>
        </div>

        <div className="header-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {/* Add/Import Group */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowAddModal(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ➕ Add Plant
            </button>
            <button
              onClick={() => {
                setShowLibraryModal(true);
                setLibrarySearch('');
                setSelectedLibraryItems(new Set());
                setLibraryCheckInventory(false);
                setLibraryAvailability({});
                setLibraryAvailabilityError('');
                lastLibraryAvailabilitySignatureRef.current = '';
                fetchDesignerLibrary('');
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Add multiple plants from your Master Plant List (catalog from all your projects)"
            >
              📚 Add from Master
            </button>
            <button
              onClick={() => {
                setCsvRawRecords([]);
                setCsvHeaders([]);
                setCsvIgnoredBlankRows(0);
                setCsvColumnMapping(emptyProjectCsvColumnMapping());
                setCsvPreview([]);
                setCsvIncludedRows({});
                setCsvImportStatus('');
                setCsvImportResult(null);
                setCsvFile(null);
                setShowCsvModal(true);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📥 Import Plant List
            </button>
          </div>

          {/* Garden App preparation and later-phase nursery ordering tools */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #c4b5fd',
                backgroundColor: '#f5f3ff',
                maxWidth: '420px'
              }}
            >
              <button
                onClick={aiFillMissing}
                disabled={items.length === 0 || aiBulkFilling}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  cursor: items.length === 0 || aiBulkFilling ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  opacity: items.length === 0 ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                title="Generates plant care and seasonal details for the customer Garden App. Large lists can take several minutes; keep this tab open. You can review and edit results afterward."
              >
                {aiBulkFilling
                  ? `Generating Garden App care… ${aiBulkProgress.done}/${aiBulkProgress.total}`
                  : 'Generate Garden App Care Info'}
              </button>
              <div style={{ color: '#5b21b6', fontSize: '12px', lineHeight: 1.35 }}>
                Fills missing sun, water, soil, hardiness, seasonal tasks, and care notes used for the customer Garden App. You can review/edit this info before the next handoff steps. Repeat lookups for the same plant identity use a shared server cache and skip extra AI calls; care is also merged onto your Designer Master Plant List row when linked.
              </div>
            </div>
            <details style={{ position: 'relative' }}>
              <summary
                style={{
                  listStyle: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Later-phase nursery lookup, ordering, and CSV export tools"
              >
                Nursery ordering ▾
              </summary>
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  zIndex: 20,
                  minWidth: '260px',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <div style={{ color: '#6b7280', fontSize: '12px', lineHeight: 1.35 }}>
                  Later phase tools for checking nursery availability and preparing nursery order/export files.
                </div>
                <button
                  onClick={() => {
                    setShowSupplierAvailability(true);
                    lastSupplierAvailabilitySignatureRef.current = '';
                    fetchSupplierAvailability();
                  }}
                  disabled={items.length === 0 || loadingAvailability}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: items.length === 0 || loadingAvailability ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    opacity: items.length === 0 ? 0.5 : 1,
                    textAlign: 'left'
                  }}
                  title="Check supplier inventory matches and show the Supplier Availability column"
                >
                  {loadingAvailability ? 'Checking availability…' : (showSupplierAvailability ? 'Refresh nursery availability' : 'Check nursery availability')}
                </button>
                {showSupplierAvailability && (
                  <button
                    onClick={() => setShowSupplierAvailability(false)}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      textAlign: 'left'
                    }}
                    title="Hide Supplier Availability column"
                  >
                    Hide nursery availability column
                  </button>
                )}
                <button
                  onClick={exportOrderCSV}
                  disabled={items.length === 0}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    opacity: items.length === 0 ? 0.5 : 1,
                    textAlign: 'left'
                  }}
                  title="Export an order CSV grouped by supplier matches"
                >
                  Export nursery order
                </button>
                <button
                  onClick={exportToCSV}
                  disabled={items.length === 0}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    opacity: items.length === 0 ? 0.5 : 1,
                    textAlign: 'left'
                  }}
                  title="Export the current plant list as CSV"
                >
                  Export plant list CSV
                </button>
              </div>
            </details>
            <button
              onClick={generatePlantListPDF}
              disabled={items.length === 0}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: 'white',
                cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                opacity: items.length === 0 ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Generate printable plant list PDF"
            >
              🖨️ Print Plant List
            </button>
          </div>

          {aiFillError && (
            <div
              role="alert"
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%'
              }}
            >
              <span>⚠️ {aiFillError}</span>
              <button
                onClick={() => setAiFillError('')}
                style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '14px' }}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          )}

          {/* Symbols - Always available (bulk apply uses selected or filtered scope) */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleSelectAll(true)}
              disabled={visibleItems.length === 0}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                cursor: visibleItems.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                opacity: visibleItems.length === 0 ? 0.5 : 1
              }}
              title="Select all currently shown rows (respects filters/search)"
            >
              Select all shown ({visibleItems.length})
            </button>
            <button
              type="button"
              onClick={() => handleSelectAll(false)}
              disabled={selectedItems.size === 0}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                cursor: selectedItems.size === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                opacity: selectedItems.size === 0 ? 0.5 : 1
              }}
              title="Clear selection"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={() =>
                markBulkCareReviewed(Array.from(selectedItems), `Mark ${selectedItems.size} selected plant${selectedItems.size === 1 ? '' : 's'} as reviewed where they have Garden App AI care? Rows without AI care are skipped.`)}
              disabled={selectedItems.size === 0 || bulkMarkingReviewed}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #86efac',
                backgroundColor: '#ecfdf5',
                color: '#166534',
                cursor: selectedItems.size === 0 || bulkMarkingReviewed ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                opacity: selectedItems.size === 0 ? 0.5 : 1
              }}
              title="Mark selected rows as designer-reviewed (only rows that already have AI-filled care)"
            >
              {bulkMarkingReviewed ? '⏳ Marking…' : `✓ Mark selected reviewed (${selectedItems.size})`}
            </button>
            <button
              type="button"
              onClick={() =>
                markBulkCareReviewed(
                  visibleNeedingCareReviewIds,
                  `Mark ${visibleNeedingCareReviewIds.length} shown plant${visibleNeedingCareReviewIds.length === 1 ? '' : 's'} as reviewed? These are rows that currently have AI care and are not yet marked reviewed (current filters apply).`
                )}
              disabled={visibleNeedingCareReviewIds.length === 0 || bulkMarkingReviewed}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #bbf7d0',
                backgroundColor: '#f0fdf4',
                color: '#15803d',
                cursor: visibleNeedingCareReviewIds.length === 0 || bulkMarkingReviewed ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                opacity: visibleNeedingCareReviewIds.length === 0 ? 0.5 : 1
              }}
              title="Mark every currently visible row that has AI care and still needs review (respects filters)"
            >
              {bulkMarkingReviewed ? '⏳ Marking…' : `✓ Mark all shown needing review (${visibleNeedingCareReviewIds.length})`}
            </button>
            {selectedItems.size > 0 && (
              <button
                onClick={deleteSelectedItems}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Remove selected rows from the project plant list"
              >
                🗑️ Delete ({selectedItems.size})
              </button>
            )}
            <div
              style={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                paddingLeft: '8px',
                borderLeft: '1px solid #e5e7eb',
                marginLeft: '4px'
              }}
            >
              <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>Garden App zone</span>
              <select
                value={bulkManualZone}
                onChange={(e) => setBulkManualZone(e.target.value)}
                style={{ fontSize: '12px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '8px', maxWidth: '200px' }}
                title="Assign manual zone to selected rows (overrides map detection)"
              >
                <option value="">Unassigned</option>
                {planZones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={bulkApplyManualZoneToSelected}
                disabled={selectedItems.size === 0 || bulkApplyingZone}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #94a3b8',
                  backgroundColor: '#f8fafc',
                  color: '#334155',
                  cursor: selectedItems.size === 0 || bulkApplyingZone ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  opacity: selectedItems.size === 0 || bulkApplyingZone ? 0.5 : 1
                }}
              >
                {bulkApplyingZone ? 'Applying…' : `Apply zone (${selectedItems.size})`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="mx-4 mb-4 rounded-xl border border-slate-200 bg-slate-50/80 overflow-hidden"
        style={{ maxWidth: '1200px' }}
      >
        <button
          type="button"
          onClick={() => setShowZonesPanel((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left bg-white/80 hover:bg-white border-b border-slate-200"
        >
          <span className="font-semibold text-slate-800">Garden App zones (list and handoff)</span>
          <span className="text-slate-500 text-sm">{showZonesPanel ? 'Hide' : 'Show'}</span>
        </button>
        {showZonesPanel && (
          <div className="px-4 py-3 text-sm text-slate-700 space-y-3">
            <p className="text-xs text-slate-600 leading-relaxed">
              Define named zones here for the Garden App without drawing on the map first. New zones use a tiny placeholder shape so they save correctly; you can draw on the plan later in Zone Manager.
              {!placedPlanId ? (
                <span className="block mt-1 text-amber-800">
                  No drawing is selected in the URL — new zones are <strong>project-wide</strong> (all drawings). Open this list from a plan with <code className="text-xs bg-amber-100 px-1 rounded">?planId=…</code> to attach zones to one drawing only.
                </span>
              ) : null}
            </p>
            {planZonesError ? (
              <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">{planZonesError}</div>
            ) : null}
            {planZonesLoading ? <div className="text-xs text-slate-500">Loading zones…</div> : null}
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-0.5">New zone name</label>
                <input
                  type="text"
                  value={newZoneNameInput}
                  onChange={(e) => setNewZoneNameInput(e.target.value)}
                  placeholder="e.g. Front border"
                  className="border rounded-lg px-2 py-1.5 text-sm min-w-[180px]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-0.5">Default season</label>
                <select
                  value={newZoneSeason}
                  onChange={(e) => setNewZoneSeason(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm"
                >
                  <option value="spring">Spring</option>
                  <option value="summer">Summer</option>
                  <option value="fall">Fall</option>
                  <option value="winter">Winter</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-0.5">Sun / exposure</label>
                <select
                  value={newZoneSunExposure}
                  onChange={(e) => setNewZoneSunExposure(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm min-w-[140px]"
                >
                  <option value="">Not set</option>
                  {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="capitalize">{s}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[200px] flex-1 max-w-md">
                <label className="block text-xs font-medium text-slate-600 mb-0.5">Site notes</label>
                <textarea
                  value={newZoneSiteNotes}
                  onChange={(e) => setNewZoneSiteNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional — same as Garden App area notes"
                  className="border rounded-lg px-2 py-1.5 text-sm w-full resize-y min-h-[2.5rem]"
                />
              </div>
              <button
                type="button"
                onClick={createListOnlyZone}
                disabled={savingZone || planZonesLoading}
                className="px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900 disabled:opacity-50"
              >
                {savingZone ? 'Saving…' : 'Add zone'}
              </button>
            </div>
            {planZones.length === 0 && !planZonesLoading ? (
              <p className="text-xs text-slate-500">No zones yet — add one above, then assign plants in the table or when importing.</p>
            ) : (
              <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg bg-white">
                {planZones.map((z) => (
                  <li key={z.id} className="px-3 py-2 flex flex-wrap items-center gap-2 justify-between">
                    {editingZoneId === z.id ? (
                      <>
                        <input
                          type="text"
                          value={editZoneName}
                          onChange={(e) => setEditZoneName(e.target.value)}
                          className="border rounded px-2 py-1 text-sm flex-1 min-w-[120px]"
                        />
                        <select
                          value={editZoneSeason}
                          onChange={(e) => setEditZoneSeason(e.target.value)}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value="spring">Spring</option>
                          <option value="summer">Summer</option>
                          <option value="fall">Fall</option>
                          <option value="winter">Winter</option>
                        </select>
                        <select
                          value={editZoneSunExposure}
                          onChange={(e) => setEditZoneSunExposure(e.target.value)}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value="">Sun — not set</option>
                          {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
                            <option key={s} value={s} className="capitalize">{s}</option>
                          ))}
                        </select>
                        <textarea
                          value={editZoneSiteNotes}
                          onChange={(e) => setEditZoneSiteNotes(e.target.value)}
                          rows={2}
                          placeholder="Site notes"
                          className="border rounded px-2 py-1 text-sm w-full min-w-[180px] resize-y min-h-[2.25rem]"
                        />
                        <button
                          type="button"
                          onClick={() => saveZoneEdits(z.id)}
                          disabled={savingZone}
                          className="text-xs px-2 py-1 bg-emerald-600 text-white rounded"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingZoneId(null);
                            setEditZoneName('');
                            setEditZoneSunExposure('');
                            setEditZoneSiteNotes('');
                          }}
                          className="text-xs px-2 py-1 bg-slate-200 rounded"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{z.name}</div>
                          <div className="text-[11px] text-slate-500">
                            Season: {z.season_default || 'spring'}
                            {!z.plan_id ? ' · project-wide' : ''}
                            {z.sun_exposure ? (
                              <span className="block capitalize text-slate-600 mt-0.5">Sun: {z.sun_exposure}</span>
                            ) : null}
                            {z.site_notes ? (
                              <span className="block text-slate-600 mt-0.5 line-clamp-2" title={z.site_notes}>
                                {z.site_notes}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded hover:bg-slate-50"
                            onClick={() => {
                              setEditingZoneId(z.id);
                              setEditZoneName(z.name || '');
                              setEditZoneSeason(z.season_default || 'spring');
                              setEditZoneSunExposure(z.sun_exposure || '');
                              setEditZoneSiteNotes(z.site_notes || '');
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50"
                            onClick={() => deletePlanZone(z)}
                            disabled={savingZone}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Add Plant Manually Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6 overflow-y-auto max-h-[95vh]">
            <h3 className="text-xl font-semibold mb-4">Add Plant</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Database</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    searchPlants(e.target.value);
                    if (!selectedPlant) {
                      setNewPlantData(prev => ({ ...prev, common_name: e.target.value }));
                    }
                  }}
                  placeholder="e.g. Lavender"
                  className="flex-1 px-3 py-2 border rounded-lg"
                  autoFocus
                />
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && !selectedPlant && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg shadow-sm">
                  {searchResults.map(plant => (
                    <button
                      key={plant.id}
                      onClick={() => {
                        setSelectedPlant(plant);
                        setSearchTerm(plant.common_name);
                        setNewPlantData(prev => ({
                          ...prev,
                          common_name: plant.common_name,
                          botanical_name: plant.botanical_name || '',
                          image_url: plant.image_url || prev.image_url || ''
                        }));
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="font-semibold">{plant.common_name}</div>
                      <div className="text-xs italic text-gray-600">{plant.botanical_name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* AI lookup -> create private plant -> add to project */}
            <div className="mb-4 p-4 rounded-lg border border-purple-200 bg-purple-50">
              <div className="text-xs font-bold text-purple-700 uppercase mb-2">AI lookup (adds to your private catalogue)</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiLookupQuery}
                  onChange={(e) => setAiLookupQuery(e.target.value)}
                  placeholder="e.g. deer resistant perennials for zone 8"
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <button
                  type="button"
                  onClick={runAiLookup}
                  disabled={aiLookupLoading}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {aiLookupLoading ? 'Looking up…' : 'Lookup'}
                </button>
              </div>

              {aiLookupError && (
                <div className="mt-2 text-sm text-red-700">
                  {aiLookupError}
                </div>
              )}

              {aiSmartResults.length > 0 && (
                <div className="mt-3 bg-white rounded-lg border border-purple-200 p-3">
                  <div className="text-sm font-semibold text-gray-900 mb-2">AI suggestions</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {aiSmartResults.slice(0, 8).map((p, idx) => (
                      <button
                        key={`${p.botanical_name || p.common_name || 'ai'}-${idx}`}
                        type="button"
                        onClick={async () => {
                          const draft = {
                            common_name: p.common_name || '',
                            botanical_name: p.botanical_name || '',
                            category: p.category || 'Other',
                            mature_size: p.mature_size || '',
                            light_requirements: p.light_requirements || '',
                            water_requirements: p.water_requirements || '',
                            hardiness_zones: p.hardiness_zones || '',
                            typical_price: p.typical_price || '',
                            care_notes: p.care_notes || '',
                            growing_notes: p.growing_notes || '',
                            image_suggestion: p.image_search_term || p.image_suggestion || '',
                            image_url: p.image_url || '',
                            photo_source: p.photo_source || null,
                            photo_attribution: p.photo_attribution || null,
                            image_verified: p.image_verified || false
                          };
                          setAiDraftPlant(draft);
                          setAiSmartResults([]);
                          setAiSelectedPhotoUrl(draft.image_url || '');
                          await fetchAiPhotos(draft.botanical_name, draft.common_name);
                        }}
                        className="text-left p-3 border rounded-lg hover:bg-purple-50"
                      >
                        <div className="flex gap-3 items-start">
                          <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                            {coerceImageUrl(p.image_url) ? (
                              <img src={coerceImageUrl(p.image_url)} alt={p.common_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No image</div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-gray-900 truncate">{p.common_name || 'Unknown'}</div>
                            <div className="text-xs italic text-gray-600 truncate">{p.botanical_name || ''}</div>
                            {p.image_verified && (
                              <div className="mt-1 text-[10px] font-bold text-green-700">Verified photo</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setAiSmartResults([])}
                      className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {aiDraftPlant && (
                <div className="mt-3 bg-white rounded-lg border border-purple-200 p-3">
                  <div className="text-sm font-semibold text-gray-900">
                    {aiDraftPlant.common_name} <span className="text-gray-500 font-normal">({aiDraftPlant.botanical_name})</span>
                  </div>
                  {aiDraftPlant.image_suggestion && (
                    <div className="mt-1 text-xs text-gray-600">
                      Image suggestion: <span className="font-medium">{aiDraftPlant.image_suggestion}</span>
                    </div>
                  )}

                  <div className="mt-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] font-bold text-gray-500 uppercase">Recommended photos</div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => fetchAiPhotos(aiDraftPlant.botanical_name, aiDraftPlant.common_name)}
                          className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                          disabled={aiPhotosLoading}
                        >
                          {aiPhotosLoading ? 'Searching…' : 'Find photos'}
                        </button>
                        <button
                          type="button"
                          onClick={() => openGoogleImages(aiDraftPlant.botanical_name || aiDraftPlant.common_name)}
                          className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                        >
                          Google Images
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiDraftPlant({ ...aiDraftPlant, image_url: 'https://placehold.co/800x600?text=Plant+Image', photo_source: 'placeholder', photo_attribution: null, image_verified: false })}
                          className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          Use placeholder
                        </button>
                      </div>
                    </div>

                    {aiPhotosError && (
                      <div className="mt-2 text-sm text-red-700">{aiPhotosError}</div>
                    )}

                    {aiPhotoOptions.length > 0 ? (
                      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {aiPhotoOptions.slice(0, 12).map((photo, idx) => (
                          <button
                            key={`${photo.url}-${idx}`}
                            type="button"
                            onClick={() => selectAiPhoto(photo)}
                            className={`border rounded-lg overflow-hidden text-left hover:shadow-sm ${
                              (aiSelectedPhotoUrl && photo.url === aiSelectedPhotoUrl) ? 'border-purple-600 ring-2 ring-purple-200' : 'border-gray-200'
                            }`}
                            title={photo.attribution || photo.source || 'Photo'}
                          >
                            <div className="w-full h-20 bg-gray-100">
                              <img
                                src={photo.thumbnail || photo.url}
                                alt={photo.alt || aiDraftPlant.common_name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                            <div className="p-1">
                              <div className="text-[10px] text-gray-600 truncate">
                                {(photo.verified ? 'Verified • ' : '')}{photo.source || ''}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-600">
                        Click “Find photos” to get recommended images (verified sources first).
                      </div>
                    )}

                    {aiDraftPlant.image_url && (
                      <div className="mt-2 text-xs text-gray-700">
                        Selected photo: <span className="font-medium">{aiDraftPlant.photo_source || 'source unknown'}</span>
                        {aiDraftPlant.photo_attribution ? (
                          <> • <span className="text-gray-600">{aiDraftPlant.photo_attribution}</span></>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setAiDraftPlant(null)}
                      className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                      disabled={addingAIPlant}
                    >
                      Clear AI
                    </button>
                    <button
                      type="button"
                      onClick={addAiPlantToProject}
                      disabled={addingAIPlant}
                      className="px-3 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 disabled:bg-gray-400"
                      title="Creates a private plant record, then adds it to this project list"
                    >
                      {addingAIPlant ? 'Adding…' : 'Add AI plant to project'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {selectedPlant ? (
              <div className="bg-green-50 p-3 rounded-lg mb-4 flex justify-between items-start">
                <div>
                  <div className="text-xs font-bold text-green-600 uppercase mb-1">Database Match</div>
                  <div className="font-semibold text-green-800">{selectedPlant.common_name}</div>
                  <div className="text-xs italic text-green-700">{selectedPlant.botanical_name}</div>
                </div>
                <button 
                  onClick={() => {
                    setSelectedPlant(null);
                    setNewPlantData(prev => ({ ...prev, common_name: searchTerm }));
                  }}
                  className="text-green-800 hover:text-green-900 text-lg"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="text-xs font-bold text-blue-600 uppercase mb-3">Custom Plant Info</div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Common Name (Required)</label>
                    <input
                      type="text"
                      value={newPlantData.common_name}
                      onChange={(e) => setNewPlantData({ ...newPlantData, common_name: e.target.value })}
                      placeholder="e.g. English Lavender"
                      className="w-full px-3 py-1.5 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Botanical Name (Optional)</label>
                    <input
                      type="text"
                      value={newPlantData.botanical_name}
                      onChange={(e) => setNewPlantData({ ...newPlantData, botanical_name: e.target.value })}
                      placeholder="e.g. Lavandula angustifolia"
                      className="w-full px-3 py-1.5 border rounded-lg text-sm italic"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Image */}
            <div className="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-bold text-gray-600 uppercase">Image</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchAddPhotos(selectedPlant?.botanical_name || newPlantData.botanical_name, selectedPlant?.common_name || newPlantData.common_name)}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                    disabled={addPhotosLoading}
                    title="Find recommended photos (verified sources first)"
                  >
                    {addPhotosLoading ? 'Searching…' : 'Find photos'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openGoogleImages(selectedPlant?.botanical_name || newPlantData.botanical_name || selectedPlant?.common_name || newPlantData.common_name)}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                    title="Search Google Images in a new tab"
                  >
                    Google Images
                  </button>
                </div>
              </div>

              <input
                type="url"
                value={newPlantData.image_url}
                onChange={(e) => setNewPlantData({ ...newPlantData, image_url: e.target.value })}
                placeholder="Image URL (optional if DB plant already has one)"
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />

              {selectedPlant?.image_url && !newPlantData.image_url && (
                <div className="mt-2 text-xs text-gray-600">
                  This database plant already has an image.
                </div>
              )}

              {addPhotosError && (
                <div className="mt-2 text-sm text-red-700">{addPhotosError}</div>
              )}

              {addPhotoOptions.length > 0 && (
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {addPhotoOptions.slice(0, 12).map((photo, idx) => (
                    <button
                      key={`${photo.url}-${idx}`}
                      type="button"
                      onClick={() => selectAddPhoto(photo)}
                      className={`border rounded-lg overflow-hidden text-left hover:shadow-sm ${
                        (addSelectedPhotoUrl && photo.url === addSelectedPhotoUrl) ? 'border-emerald-600 ring-2 ring-emerald-200' : 'border-gray-200'
                      }`}
                      title={photo.attribution || photo.source || 'Photo'}
                    >
                      <div className="w-full h-20 bg-gray-100">
                        <img
                          src={photo.thumbnail || photo.url}
                          alt={photo.alt || (selectedPlant?.common_name || newPlantData.common_name)}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                      <div className="p-1">
                        <div className="text-[10px] text-gray-600 truncate">
                          {(photo.verified ? 'Verified • ' : '')}{photo.source || ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={newPlantData.quantity}
                  onChange={(e) => setNewPlantData({ ...newPlantData, quantity: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pot Size</label>
                <input
                  type="text"
                  value={newPlantData.pot_size}
                  onChange={(e) => setNewPlantData({ ...newPlantData, pot_size: e.target.value })}
                  placeholder="#5"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colour</label>
                <input
                  type="text"
                  value={newPlantData.color}
                  onChange={(e) => setNewPlantData({ ...newPlantData, color: e.target.value })}
                  placeholder="e.g. Purple"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div />
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                <input
                  type="number"
                  step="0.1"
                  value={newPlantData.width}
                  onChange={(e) => setNewPlantData({ ...newPlantData, width: e.target.value })}
                  placeholder="e.g. 3"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                <input
                  type="number"
                  step="0.1"
                  value={newPlantData.height}
                  onChange={(e) => setNewPlantData({ ...newPlantData, height: e.target.value })}
                  placeholder="e.g. 2"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expo</label>
                <input
                  type="text"
                  value={newPlantData.exposure}
                  onChange={(e) => setNewPlantData({ ...newPlantData, exposure: e.target.value })}
                  placeholder="S/PS"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPlantData.unit_price}
                  onChange={(e) => setNewPlantData({ ...newPlantData, unit_price: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={newPlantData.code}
                  onChange={(e) => setNewPlantData({ ...newPlantData, code: e.target.value })}
                  placeholder="LAV"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <input
                  type="text"
                  value={newPlantData.plant_type}
                  onChange={(e) => setNewPlantData({ ...newPlantData, plant_type: e.target.value })}
                  placeholder="OG"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newPlantData.status}
                  onChange={(e) => setNewPlantData({ ...newPlantData, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="quoted">💬 Quoted</option>
                  <option value="ordered">🛒 Ordered</option>
                  <option value="received">📦 Received</option>
                  <option value="complete">✅ Complete</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordered</label>
                <input
                  type="number"
                  min="0"
                  value={newPlantData.quantity_ordered}
                  onChange={(e) => setNewPlantData({ ...newPlantData, quantity_ordered: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Received</label>
                <input
                  type="number"
                  min="0"
                  value={newPlantData.quantity_received}
                  onChange={(e) => setNewPlantData({ ...newPlantData, quantity_received: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planted</label>
                <input
                  type="number"
                  min="0"
                  value={newPlantData.quantity_planted}
                  onChange={(e) => setNewPlantData({ ...newPlantData, quantity_planted: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Garden App zone (optional)</label>
              <select
                value={newPlantData.manual_plan_zone_id || ''}
                onChange={(e) => setNewPlantData({ ...newPlantData, manual_plan_zone_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Unassigned</option>
                {planZones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Handoff uses this zone for the homeowner app when set, instead of only map placement.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={newPlantData.notes}
                onChange={(e) => setNewPlantData({ ...newPlantData, notes: e.target.value })}
                placeholder="e.g. Front yard group"
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedPlant(null);
                  setSearchTerm('');
                  setSearchResults([]);
                  setAiLookupQuery('');
                  setAiLookupError('');
                  setAiDraftPlant(null);
                  setAddPhotosLoading(false);
                  setAddPhotosError('');
                  setAddPhotoOptions([]);
                  setAddSelectedPhotoUrl('');
                  setNewPlantData({
                    quantity: 1,
                    pot_size: '',
                    color: '',
                    width: '',
                    height: '',
                    common_name: '',
                    botanical_name: '',
                    unit_price: '',
                    code: '',
                    exposure: '',
                    plant_type: '',
                    image_url: '',
                    quantity_ordered: 0,
                    quantity_received: 0,
                    quantity_planted: 0,
                    status: 'pending',
                    notes: '',
                    manual_plan_zone_id: ''
                  });
                }} 
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={addPlantManually}
                disabled={addingPlant}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {addingPlant ? 'Adding...' : 'Add to List'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add From Supplier Inventory Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl p-6 overflow-y-auto max-h-[95vh]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Add from Supplier Inventory</h3>
                <p className="text-sm text-gray-600">
                  Pick items from a supplier’s inventory and add them to this project’s plant list.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSupplierModal(false);
                  setSelectedSupplierId('');
                  setSupplierInventory([]);
                  setSupplierInventorySearch('');
                  setSelectedSupplierInventory(new Map());
                  setSupplierAddError('');
                }}
                className="text-gray-600 hover:text-gray-900 text-lg"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-wrap gap-2 items-center mb-3">
              <div className="min-w-[260px] flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Supplier</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={selectedSupplierId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setSelectedSupplierId(nextId);
                    setSupplierInventory([]);
                    setSelectedSupplierInventory(new Map());
                    if (nextId) fetchInventoryForSupplier(nextId);
                  }}
                  disabled={suppliersLoading}
                >
                  <option value="">{suppliersLoading ? 'Loading suppliers…' : 'Choose a supplier…'}</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="min-w-[260px] flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Search inventory</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg"
                  value={supplierInventorySearch}
                  onChange={(e) => setSupplierInventorySearch(e.target.value)}
                  placeholder="Search by plant/botanical/SKU…"
                />
              </div>
            </div>

            {supplierAddError && (
              <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {supplierAddError}
              </div>
            )}

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2" style={{ width: '40px' }} />
                      <th className="text-left p-2">Plant</th>
                      <th className="text-left p-2">Botanical</th>
                      <th className="text-left p-2">Pot</th>
                      <th className="text-left p-2">Size</th>
                      <th className="text-left p-2">Price</th>
                      <th className="text-left p-2">Availability</th>
                      <th className="text-left p-2">SKU</th>
                      <th className="text-left p-2">Location</th>
                      <th className="text-left p-2" style={{ width: '90px' }}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierInventoryLoading ? (
                      <tr>
                        <td className="p-3 text-gray-600" colSpan={10}>Loading inventory…</td>
                      </tr>
                    ) : (() => {
                      const q = supplierInventorySearch.trim().toLowerCase();
                      const filtered = !q ? supplierInventory : supplierInventory.filter((inv) => {
                        const hay = [
                          inv.plant_name,
                          inv.botanical_name,
                          inv.sku,
                          inv.location,
                          inv.pot,
                          inv.size
                        ].filter(Boolean).join(' ').toLowerCase();
                        return hay.includes(q);
                      });

                      if (!selectedSupplierId) {
                        return (
                          <tr>
                            <td className="p-3 text-gray-600" colSpan={10}>Choose a supplier to view inventory.</td>
                          </tr>
                        );
                      }

                      if (filtered.length === 0) {
                        return (
                          <tr>
                            <td className="p-3 text-gray-600" colSpan={10}>No inventory items found.</td>
                          </tr>
                        );
                      }

                      return filtered.map((inv) => {
                        const checked = selectedSupplierInventory.has(inv.id);
                        const qty = selectedSupplierInventory.get(inv.id) || 1;
                        return (
                          <tr key={inv.id} className="border-t">
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSupplierInventorySelection(inv.id, qty)}
                              />
                            </td>
                            <td className="p-2 font-semibold">{inv.plant_name || '-'}</td>
                            <td className="p-2 italic text-gray-700">{inv.botanical_name || '-'}</td>
                            <td className="p-2">{inv.pot || '-'}</td>
                            <td className="p-2">{inv.size || '-'}</td>
                            <td className="p-2">{inv.price != null ? `$${inv.price}` : '-'}</td>
                            <td className="p-2">{inv.availability || '-'}</td>
                            <td className="p-2">{inv.sku || '-'}</td>
                            <td className="p-2">{inv.location || '-'}</td>
                            <td className="p-2">
                              <input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => setSupplierInventoryQty(inv.id, e.target.value)}
                                className="w-full px-2 py-1 border rounded"
                                disabled={!checked}
                              />
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4 gap-3">
              <div className="text-sm text-gray-600">
                Selected: <span className="font-semibold">{selectedSupplierInventory.size}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSelectedSupplierInventory(new Map());
                    setSupplierAddError('');
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  disabled={selectedSupplierInventory.size === 0}
                >
                  Clear selection
                </button>
                <button
                  onClick={addSelectedFromSupplierInventory}
                  className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400"
                  disabled={selectedSupplierInventory.size === 0 || addingFromSupplier}
                >
                  {addingFromSupplier ? 'Adding…' : 'Add to Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add From Master Plant List (Designer Library) Modal */}
      {showLibraryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl p-6 overflow-y-auto max-h-[95vh]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Add from Master Plant List</h3>
                <p className="text-sm text-gray-600">
                  Pick rows from your Master Plant List (all projects) to copy onto this project&apos;s Project Plant List. Quantities default to 1; you can edit after.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowLibraryModal(false);
                  setLibrarySearch('');
                  setLibraryItems([]);
                  setSelectedLibraryItems(new Set());
                }}
                className="text-gray-600 hover:text-gray-900 text-lg"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-wrap gap-2 items-center mb-3">
              <input
                type="text"
                value={librarySearch}
                onChange={(e) => {
                  const q = e.target.value;
                  setLibrarySearch(q);
                  fetchDesignerLibrary(q);
                }}
                placeholder="Search your library…"
                className="flex-1 min-w-[240px] px-3 py-2 border rounded-lg"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 px-3 py-2 border rounded-lg bg-white">
                <input
                  type="checkbox"
                  checked={libraryCheckInventory}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setLibraryCheckInventory(next);
                    setLibraryAvailabilityError('');
                    setLibraryAvailability({});
                    lastLibraryAvailabilitySignatureRef.current = '';
                    if (next && libraryItems.length > 0) fetchLibraryAvailability(libraryItems);
                  }}
                />
                Check inventory
                {libraryAvailabilityLoading && <span className="text-xs text-gray-500">Checking…</span>}
              </label>
              <button
                onClick={() => {
                  setLibrarySearch('');
                  fetchDesignerLibrary('');
                }}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  // Select all currently visible rows
                  setSelectedLibraryItems(new Set(libraryItems.map(i => i.id)));
                }}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={libraryItems.length === 0}
              >
                Select all
              </button>
              <button
                onClick={() => setSelectedLibraryItems(new Set())}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={selectedLibraryItems.size === 0}
              >
                Clear selection
              </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto">
                {libraryCheckInventory && libraryAvailabilityError && (
                  <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border-b">
                    {libraryAvailabilityError}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2" style={{ width: '40px' }} />
                      <th className="text-left p-2">Common</th>
                      <th className="text-left p-2">Botanical</th>
                      <th className="text-left p-2">Size</th>
                      <th className="text-left p-2">Colour</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Price</th>
                      <th className="text-left p-2">Inventory</th>
                    </tr>
                  </thead>
                  <tbody>
                    {libraryLoading ? (
                      <tr>
                        <td colSpan={8} className="p-4 text-gray-600">Loading…</td>
                      </tr>
                    ) : libraryItems.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-4 text-gray-600">No items found.</td>
                      </tr>
                    ) : (
                      libraryItems.map(li => (
                        <tr key={li.id} className="border-t hover:bg-gray-50">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedLibraryItems.has(li.id)}
                              onChange={() => toggleSelectLibraryItem(li.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td className="p-2 font-semibold">{li.common_name}</td>
                          <td className="p-2 italic text-gray-700">{li.botanical_name || '-'}</td>
                          <td className="p-2">{li.pot_size || '-'}</td>
                          <td className="p-2">{li.color || '-'}</td>
                          <td className="p-2">{li.code || '-'}</td>
                          <td className="p-2">{li.unit_price != null ? `$${li.unit_price}` : '-'}</td>
                          <td className="p-2">
                            {!libraryCheckInventory ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : (() => {
                              const key = getLibraryAvailabilityKey(li);
                              const matches = key ? (libraryAvailability?.[key] || []) : [];
                              if (!key) return <span className="text-xs text-gray-400">—</span>;
                              if (!matches || matches.length === 0) return <span className="text-xs text-gray-500">No match</span>;

                              const supplierNames = Array.from(new Set(matches.map((m) => m.supplier_name).filter(Boolean)));
                              const prices = matches.map((m) => Number(m.price)).filter((n) => !Number.isNaN(n));
                              const best = prices.length ? Math.min(...prices) : null;
                              const title = matches
                                .slice(0, 10)
                                .map((m) => `${m.supplier_name || 'Supplier'} • ${m.pot || m.size || '-'} • ${m.price != null ? `$${m.price}` : '-'} • ${m.availability || '-'}`)
                                .join('\n');

                              return (
                                <span className="text-xs text-green-700" title={title}>
                                  ✅ {supplierNames.length} supplier{supplierNames.length === 1 ? '' : 's'}
                                  {best != null ? ` • from $${best}` : ''}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                Selected: {selectedLibraryItems.size}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowLibraryModal(false);
                    setLibrarySearch('');
                    setLibraryItems([]);
                    setSelectedLibraryItems(new Set());
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedFromLibrary}
                  disabled={selectedLibraryItems.size === 0 || addingFromLibrary}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {addingFromLibrary ? 'Adding…' : 'Add to Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal — simple steps + column matching */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          {csvImporting && (
            <div className="fixed inset-0 z-[60] bg-black/35 flex items-center justify-center p-4">
              <div
                role="status"
                aria-live="assertive"
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-green-100 p-6 text-center"
              >
                <div className="mx-auto mb-4 h-12 w-12 rounded-full border-4 border-green-200 border-t-green-600 animate-spin" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">Uploading plants…</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {csvImportStatus || 'Uploading plants to your project. This can take a moment for large spreadsheets.'}
                </p>
                <p className="mt-3 text-xs text-gray-500">Please keep this window open.</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[96vh] overflow-y-auto p-4 sm:p-6 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Add plants from a spreadsheet</h3>
            <p className="text-base text-gray-700 mb-3 leading-relaxed">
              Choose your <strong>CSV</strong> file. We guess your columns for you, then show exactly what will import.
              Uncheck rows you want to ignore before adding them to the project.
            </p>
            <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-950 leading-relaxed">
              <strong className="block mb-1">What is a CSV?</strong>
              A CSV is a spreadsheet saved as a simple file. In Google Sheets, use <strong>File &gt; Download &gt; Comma-separated values (.csv)</strong>. In Excel, use <strong>Save As</strong> and choose <strong>CSV</strong>. Then choose that file here.
            </div>
            <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-950 leading-relaxed">
              <strong className="block mb-1">Simple import</strong>
              We import only the rows in this file. We do not search Plantaday or your master library for matches during CSV import.
            </div>

            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files[0];
                setCsvFile(file || null);
                if (file) parseCsvFile(file);
              }}
              className="mb-4 block w-full text-sm rounded-lg border border-gray-200 px-3 py-2 bg-gray-50"
            />

            {csvParsing && (
              <div className="text-center py-6 text-gray-700 text-lg font-medium">Working on your file…</div>
            )}

            {csvHeaders.length > 0 && (
              <div className="mb-5 rounded-xl border-2 border-amber-100 bg-amber-50/40 p-4">
                <h4 className="text-base font-bold text-gray-900 mb-1">Match your columns</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Please check every dropdown before previewing. Scroll through this whole yellow box to make sure each field is matched correctly. “Skip” means your spreadsheet does not have that column.
                </p>
                <div className="mb-3 rounded-lg border border-amber-100 bg-white px-3 py-2 text-sm text-gray-800">
                  Parsed <strong>{csvRawRecords.length}</strong> data rows from this CSV.
                  {csvIgnoredBlankRows > 0 ? (
                    <span className="block mt-1 text-gray-700">
                      Ignored {csvIgnoredBlankRows} blank spreadsheet rows.
                    </span>
                  ) : null}
                  {csvRawRecords.length > 200 ? (
                    <span className="block mt-1 text-amber-900">
                      This is a large import. If that number looks too high, cancel and check the CSV export before importing.
                    </span>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 max-h-56 overflow-y-auto pr-1 border border-amber-100 rounded-lg p-2 bg-white">
                  {PROJECT_CSV_FIELD_ORDER.map(({ key, label }) => (
                    <div key={key} className="flex flex-col">
                      <label className="text-xs font-medium text-gray-600 mb-0.5">{label}</label>
                      <select
                        className="border rounded-lg px-2 py-2 text-sm bg-white"
                        value={csvColumnMapping[key] || ''}
                        onChange={(e) => {
                          setCsvColumnMapping((prev) => ({
                            ...prev,
                            [key]: e.target.value
                          }));
                          setCsvPreview([]);
                          setCsvIncludedRows({});
                        }}
                      >
                        <option value="">— Skip —</option>
                        {csvHeaders.map((h) => (
                          <option key={`${key}-${h}`} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => buildCsvPreview()}
                  disabled={csvParsing || !csvRawRecords.length}
                  className="mt-4 w-full sm:w-auto px-6 py-4 rounded-2xl bg-green-600 text-white text-lg font-bold hover:bg-green-700 disabled:bg-gray-400 shadow-md"
                >
                  {csvParsing ? 'Checking mapped rows…' : 'Preview mapped rows'}
                </button>
              </div>
            )}

            {csvPreview.length > 0 && (
              <div className="mb-5">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-1">Review mapped rows</h4>
                    <p className="text-sm text-gray-600">
                      Parsed {csvPreview.length} rows from the CSV. {csvSelectedCount} of {csvImportableCount} importable rows selected.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {csvMissingCommonNameCount > 0 && (
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-purple-300 bg-purple-50 text-purple-800 text-sm font-medium hover:bg-purple-100 disabled:opacity-60"
                        onClick={enrichCsvMissingCommonNames}
                        disabled={csvEnrichingNames || csvParsing || csvImporting}
                        title="Use AI only to suggest missing common names for botanical-only rows. This does not match photos or plant IDs."
                      >
                        {csvEnrichingNames ? 'Finding names…' : `Find missing common names (${csvMissingCommonNameCount})`}
                      </button>
                    )}
                    <button
                      type="button"
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
                      onClick={() => {
                        const next = {};
                        csvPreview.forEach((row, idx) => {
                          if (row._importable) next[idx] = true;
                        });
                        setCsvIncludedRows(next);
                      }}
                    >
                      Select importable
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
                      onClick={() => setCsvIncludedRows({})}
                    >
                      Select none
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mb-3 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2">
                  Check that the mapped values look right. Uncheck bad rows before importing. The optional common-name lookup only fills blank common names; it does not assign photos or Plantaday IDs.
                </p>
                <div className="md:hidden max-h-[52vh] overflow-y-auto space-y-3 pr-1">
                  {csvPreview.map((row, idx) => (
                    <div key={idx} className={`rounded-xl border p-3 ${row._importable ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!csvIncludedRows[idx]}
                          disabled={!row._importable}
                          onChange={(e) =>
                            setCsvIncludedRows((prev) => ({
                              ...prev,
                              [idx]: e.target.checked
                            }))
                          }
                          aria-label={`Import CSV row ${idx + 1}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-gray-500 uppercase">Row {idx + 1}</div>
                          <div className="font-semibold text-gray-900">{row.common_name || 'No common name'}</div>
                          <div className="text-sm italic text-gray-600">{row.botanical_name || 'No scientific name'}</div>
                          {row._commonNameSource ? (
                            <div className="mt-1 text-xs font-medium text-green-700">Common name filled from {row._commonNameSource}.</div>
                          ) : null}
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                            <div><span className="font-semibold">Qty:</span> {row.quantity || 1}</div>
                            <div><span className="font-semibold">Pot:</span> {row.pot_size || '—'}</div>
                            <div><span className="font-semibold">Code:</span> {row.code || '—'}</div>
                            <div><span className="font-semibold">Photo:</span> {row.image_url ? 'Yes' : '—'}</div>
                            <div className="col-span-2">
                              <span className="font-semibold">Garden App zone:</span>{' '}
                              {row._csv_zone_warning ? (
                                <span className="text-amber-800">{row._csv_zone_warning}</span>
                              ) : row._csv_manual_zone_id ? (
                                planZones.find((z) => z.id === row._csv_manual_zone_id)?.name || row._csv_manual_zone_id
                              ) : (
                                '—'
                              )}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-600">
                            {row._reviewNote || row.notes || 'No notes'}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
                <div className="hidden md:block max-h-[52vh] overflow-auto border border-gray-200 rounded-xl">
                  <table className="min-w-[1020px] w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Import</th>
                        <th className="px-3 py-2 text-left">Row</th>
                        <th className="px-3 py-2 text-left">Common name</th>
                        <th className="px-3 py-2 text-left">Scientific name</th>
                        <th className="px-3 py-2 text-left">Qty</th>
                        <th className="px-3 py-2 text-left">Pot</th>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-left">Photo link</th>
                        <th className="px-3 py-2 text-left">Garden App zone</th>
                        <th className="px-3 py-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {csvPreview.map((row, idx) => (
                        <tr key={idx} className={row._importable ? 'bg-white' : 'bg-gray-50 text-gray-500'}>
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={!!csvIncludedRows[idx]}
                              disabled={!row._importable}
                              onChange={(e) =>
                                setCsvIncludedRows((prev) => ({
                                  ...prev,
                                  [idx]: e.target.checked
                                }))
                              }
                              aria-label={`Import CSV row ${idx + 1}`}
                            />
                          </td>
                          <td className="px-3 py-2 align-top tabular-nums">{idx + 1}</td>
                          <td className="px-3 py-2 align-top font-medium text-gray-900">{row.common_name || '—'}</td>
                          <td className="px-3 py-2 align-top italic">{row.botanical_name || '—'}</td>
                          <td className="px-3 py-2 align-top tabular-nums">{row.quantity || 1}</td>
                          <td className="px-3 py-2 align-top">{row.pot_size || '—'}</td>
                          <td className="px-3 py-2 align-top">{row.code || '—'}</td>
                          <td className="px-3 py-2 align-top max-w-[160px] truncate" title={row.image_url || ''}>
                            {row.image_url || '—'}
                          </td>
                          <td className="px-3 py-2 align-top max-w-[140px] text-xs">
                            {row._csv_zone_warning ? (
                              <span className="text-amber-800">{row._csv_zone_warning}</span>
                            ) : row._csv_manual_zone_id ? (
                              planZones.find((z) => z.id === row._csv_manual_zone_id)?.name || row._csv_manual_zone_id
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 align-top max-w-[220px]">
                            <div className="line-clamp-2" title={row._reviewNote || row.notes || ''}>
                              {row._reviewNote || row.notes || (row._commonNameSource ? `Common name filled from ${row._commonNameSource}.` : '—')}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {csvImporting && (
              <div
                role="status"
                aria-live="polite"
                className="mb-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-950"
              >
                Uploading plants to your project… This can take a moment if your spreadsheet has several rows.
              </div>
            )}

            <div className="sticky bottom-0 bg-white flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowCsvModal(false);
                  setCsvFile(null);
                  setCsvRawRecords([]);
                  setCsvHeaders([]);
                  setCsvIgnoredBlankRows(0);
                  setCsvColumnMapping(emptyProjectCsvColumnMapping());
                  setCsvPreview([]);
                  setCsvIncludedRows({});
                  setCsvImportStatus('');
                }}
                disabled={csvImporting}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={importCsvToMasterList}
                disabled={!csvSelectedCount || csvImporting}
                className="px-6 py-3 rounded-2xl bg-green-600 text-white text-lg font-bold hover:bg-green-700 disabled:bg-gray-400 shadow-md"
              >
                {csvImporting ? 'Uploading plants…' : `Import selected rows (${csvSelectedCount})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {csvImportResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Plantaday says</h3>
            <p className="text-base font-semibold text-gray-900 mb-3">Import complete!</p>
            <div className="space-y-1 text-sm text-gray-800">
              <div>Created: <span className="font-semibold">{csvImportResult.created}</span></div>
              <div>Updated: <span className="font-semibold">{csvImportResult.updated}</span></div>
              <div>Failed: <span className="font-semibold">{csvImportResult.failed}</span></div>
              <div>Skipped: <span className="font-semibold">{csvImportResult.skipped_empty}</span></div>
            </div>
            {csvImportResult.zoneWarnings?.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50 p-3">
                <div className="text-sm font-semibold text-amber-950 mb-2">Garden App zone column</div>
                <p className="text-xs text-amber-950 mb-2">
                  These rows were still imported, but the zone value could not be matched. Fix the spelling or add the zone in the list above, then assign zones on those rows.
                </p>
                <div className="max-h-36 overflow-y-auto space-y-1 text-xs text-amber-950">
                  {csvImportResult.zoneWarnings.map((zw, idx) => (
                    <div key={`${zw.rowNumber}-zw-${idx}`}>
                      Row {zw.rowNumber}{zw.label ? ` (${zw.label})` : ''}: {zw.message}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {csvImportResult.skippedRows?.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50 p-3">
                <div className="text-sm font-semibold text-amber-950 mb-2">Skipped rows</div>
                <div className="max-h-36 overflow-y-auto space-y-1 text-xs text-amber-950">
                  {csvImportResult.skippedRows.map((row, idx) => (
                    <div key={`${row.rowNumber}-${idx}`}>
                      Row {row.rowNumber}{row.label ? ` (${row.label})` : ''}: {row.reason}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setCsvImportResult(null)}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfBlobUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Plant List Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadPDF}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  💾 Save PDF
                </button>
                <button
                  onClick={closePdfPreview}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={pdfBlobUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <p>📋 No plants in project plant list yet</p>
          <p className="hint">
            Add plants with <strong>Auto-Detect Plants</strong> on the plan, <strong>manual add</strong>, <strong>import plant list</strong>, or <strong>Add from Master</strong> (your Master Plant List from all projects).
          </p>
          <button className="primary-button" onClick={() => navigate(`/plan/${id}`)}>
            Go to Project
          </button>
        </div>
      ) : (
        <div className="master-list-table-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                Placed
              </div>
              <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setPlacedScope('current')}
                  className="px-3 py-1 text-xs"
                  style={{
                    background: placedScope === 'current' ? '#e0f2fe' : 'white',
                    color: placedScope === 'current' ? '#0369a1' : '#374151',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  title="Show placed counts for the current drawing"
                >
                  Current drawing
                </button>
                <button
                  type="button"
                  onClick={() => setPlacedScope('all')}
                  className="px-3 py-1 text-xs"
                  style={{
                    background: placedScope === 'all' ? '#e0f2fe' : 'white',
                    color: placedScope === 'all' ? '#0369a1' : '#374151',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  title="Show placed counts across all drawings in this project"
                >
                  All drawings
                </button>
              </div>
              {placementsLoading && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Loading placed…
                </div>
              )}
              {!placementsLoading && placementsError && (
                <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                  {placementsError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ fontSize: '12px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                title="Filter rows by Type"
              >
                <option value="">All Types</option>
                <option value="__missing__">Missing Type</option>
                {availableTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                value={missingFilter}
                onChange={(e) => setMissingFilter(e.target.value)}
                style={{ fontSize: '12px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                title="Filter to show rows missing a specific field"
              >
                <option value="">Show all</option>
                <option value="common_name">Missing Common</option>
                <option value="botanical_name">Missing Botanical</option>
                <option value="image_url">Missing Image Link</option>
                <option value="code">Missing Code</option>
              </select>
              {missingFilter && (
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  Showing {visibleItems.length} of {items.length}
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#6b7280' }} title="Placed counts are calculated from markers on the plan (sum of placement quantities).">
                Sum of plan marker quantities
              </div>
            </div>
          </div>
          <table className="master-list-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={visibleItems.length > 0 && visibleItems.every((it) => selectedItems.has(it.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th>Qty</th>
                <th>Placed</th>
                <th>Size</th>
                <th>Colour</th>
                <th>Botanical Name</th>
                <th>Common Name</th>
                <th>Code</th>
                <th>Width</th>
                <th>Height</th>
                <th>Expo</th>
                <th>Type</th>
                <th title="Manual Garden App / handoff zone (overrides map placement)">Zone</th>
                <th>Ordered</th>
                <th>Received</th>
                <th>Planted</th>
                <th>Status</th>
                {showSupplierAvailability && <th>Supplier Availability</th>}
                <th>Image</th>
                <th>Notes</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map(item => (
                <React.Fragment key={item.id}>
                <tr 
                  id={`mpl-row-${item.id}`}
                  className={editingItem === item.id ? 'editing' : ''}
                  style={{ 
                    backgroundColor: selectedItems.has(item.id) ? '#e0f2fe' : 'transparent' 
                  }}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  {/* Quantity */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        value={editValues.quantity}
                        onChange={(e) => setEditValues({...editValues, quantity: parseInt(e.target.value) || 0})}
                        min="0"
                        className="edit-input small"
                      />
                    ) : (
                      <strong>{item.quantity}</strong>
                    )}
                  </td>
                  {/* Placed (sum of placement quantities) */}
                  <td>
                    {(() => {
                      let placed = placedMaps.byMaster?.[item.id] || 0;
                      if (!placed && item.plant_id && !placedMaps.hasAnyMasterListId) {
                        placed = placedMaps.byPlant?.[item.plant_id] || 0;
                      }
                      const qty = Number(item.quantity) || 0;
                      const title = placedScope === 'current'
                        ? `Placed on current drawing: ${placed}${qty ? ` (project qty: ${qty})` : ''}`
                        : `Placed on all drawings: ${placed}${qty ? ` (project qty: ${qty})` : ''}`;
                      return (
                        <div title={title} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <strong>{placed}</strong>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>
                            {qty ? `of ${qty}` : ''}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  {/* Pot Size */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="text"
                        value={editValues.pot_size}
                        onChange={(e) => setEditValues({...editValues, pot_size: e.target.value})}
                        placeholder="#5"
                        className="edit-input small"
                      />
                    ) : (
                      item.pot_size || '-'
                    )}
                  </td>
                  {/* Colour */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="text"
                        value={editValues.color}
                        onChange={(e) => setEditValues({...editValues, color: e.target.value})}
                        placeholder="Colour"
                        className="edit-input small"
                      />
                    ) : (
                      item.color || '-'
                    )}
                  </td>
                  {/* Botanical Name */}
                  <td className="botanical-name">
                    <em>{item.botanical_name || '-'}</em>
                  </td>
                  {/* Common Name */}
                  <td className="plant-name">
                    {item.common_name || 'Unknown'}
                  </td>
                  {/* Code */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="text"
                        value={editValues.code}
                        onChange={(e) => setEditValues({...editValues, code: e.target.value})}
                        placeholder="Code"
                        className="edit-input small"
                      />
                    ) : (
                      item.code || '-'
                    )}
                  </td>
                  {/* Width */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        step="0.1"
                        value={editValues.width}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditValues({ ...editValues, width: v === '' ? '' : parseFloat(v) });
                        }}
                        placeholder="W"
                        className="edit-input tiny"
                        style={{width: '60px'}}
                      />
                    ) : (
                      item.width ?? '-'
                    )}
                  </td>
                  {/* Height */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        step="0.1"
                        value={editValues.height}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditValues({ ...editValues, height: v === '' ? '' : parseFloat(v) });
                        }}
                        placeholder="H"
                        className="edit-input tiny"
                        style={{width: '60px'}}
                      />
                    ) : (
                      item.height ?? '-'
                    )}
                  </td>
                  {/* Exposure */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="text"
                        value={editValues.exposure}
                        onChange={(e) => setEditValues({...editValues, exposure: e.target.value})}
                        placeholder="S/PS"
                        className="edit-input small"
                      />
                    ) : (
                      item.exposure || '-'
                    )}
                  </td>
                  {/* Type */}
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="text"
                        value={editValues.plant_type}
                        onChange={(e) => setEditValues({...editValues, plant_type: e.target.value})}
                        placeholder="OG"
                        className="edit-input small"
                      />
                    ) : (
                      item.plant_type || '-'
                    )}
                  </td>
                  <td style={{ minWidth: '140px', maxWidth: '180px' }}>
                    <select
                      value={item.manual_plan_zone_id || ''}
                      disabled={manualZoneSavingRowId === item.id}
                      onChange={(e) => patchItemManualZone(item.id, e.target.value)}
                      className="edit-input small"
                      style={{ width: '100%', fontSize: '11px' }}
                      title="Garden App zone (manual). Cleared or empty uses map placement when available."
                    >
                      <option value="">Unassigned</option>
                      {planZones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        value={editValues.quantity_ordered}
                        onChange={(e) => setEditValues({...editValues, quantity_ordered: parseInt(e.target.value) || 0})}
                        min="0"
                        className="edit-input small"
                      />
                    ) : (
                      item.quantity_ordered || 0
                    )}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        value={editValues.quantity_received}
                        onChange={(e) => setEditValues({...editValues, quantity_received: parseInt(e.target.value) || 0})}
                        min="0"
                        className="edit-input small"
                      />
                    ) : (
                      item.quantity_received || 0
                    )}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        value={editValues.quantity_planted}
                        onChange={(e) => setEditValues({...editValues, quantity_planted: parseInt(e.target.value) || 0})}
                        min="0"
                        className="edit-input small"
                      />
                    ) : (
                      item.quantity_planted || 0
                    )}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <select
                        value={editValues.status}
                        onChange={(e) => setEditValues({...editValues, status: e.target.value})}
                        className="edit-input small"
                      >
                        <option value="pending">⏳ Pending</option>
                        <option value="quoted">💬 Quoted</option>
                        <option value="ordered">🛒 Ordered</option>
                        <option value="received">📦 Received</option>
                        <option value="complete">✅ Complete</option>
                      </select>
                    ) : (
                      getStatusBadge(item)
                    )}
                  </td>
                  {/* Supplier Availability */}
                  {showSupplierAvailability && (
                    <td>
                      {getAvailabilityBadge(item)}
                    </td>
                  )}
                  {/* Image URL */}
                  <td style={{ minWidth: '160px' }}>
                    {editingItem === item.id ? (
                      <div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <input
                            type="url"
                            value={editValues.image_url || ''}
                            onChange={(e) => setEditValues({ ...editValues, image_url: e.target.value })}
                            placeholder="https://…"
                            className="edit-input"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const b = item.botanical_name || editValues.botanical_name || '';
                              const c = item.common_name || editValues.common_name || '';
                              fetchEditPhotos({ botanicalName: b, commonName: c, itemId: item.id });
                            }}
                            className="px-2 py-1 bg-white border rounded hover:bg-gray-50"
                            disabled={editPhotosLoading}
                            title="Find recommended photos"
                            style={{ fontSize: '12px' }}
                          >
                            {editPhotosLoading && editPhotoPickerOpenFor === item.id ? '…' : 'Find'}
                          </button>
                          <button
                            type="button"
                            onClick={() => openGoogleImages(item.botanical_name || item.common_name)}
                            className="px-2 py-1 bg-white border rounded hover:bg-gray-50"
                            title="Google Images"
                            style={{ fontSize: '12px' }}
                          >
                            Google
                          </button>
                        </div>

                        {editPhotosError && editPhotoPickerOpenFor === item.id && (
                          <div className="text-xs" style={{ color: '#b91c1c', marginTop: '6px' }}>
                            {editPhotosError}
                          </div>
                        )}

                        {editPhotoOptions.length > 0 && editPhotoPickerOpenFor === item.id && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '6px' }}>
                              {editPhotoOptions.slice(0, 8).map((photo, idx) => (
                                <button
                                  key={`${photo.url}-${idx}`}
                                  type="button"
                                  onClick={() => selectEditPhoto(photo)}
                                  className="border rounded overflow-hidden hover:shadow-sm"
                                  style={{
                                    borderColor: (editSelectedPhotoUrl && photo.url === editSelectedPhotoUrl) ? '#7c3aed' : '#e5e7eb'
                                  }}
                                  title={photo.attribution || photo.source || 'Photo'}
                                >
                                  <div style={{ width: '100%', height: '46px', background: '#f3f4f6' }}>
                                    <img
                                      src={photo.thumbnail || photo.url}
                                      alt={photo.alt || item.common_name || 'Plant'}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  </div>
                                  <div style={{ padding: '2px 4px', fontSize: '10px', color: '#6b7280', textAlign: 'left' }}>
                                    {(photo.verified ? 'Verified • ' : '')}{photo.source || ''}
                                  </div>
                                </button>
                              ))}
                            </div>
                            <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ fontSize: '10px', color: '#6b7280' }}>
                                Click a thumbnail to set image.
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditPhotoPickerOpenFor(null);
                                  setEditPhotoOptions([]);
                                  setEditPhotosError('');
                                }}
                                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                                style={{ fontSize: '11px' }}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : coerceImageUrl(item.image_url) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={coerceImageUrl(item.image_url)}
                          alt={item.common_name || 'Plant'}
                          style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e5e7eb' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <a
                          href={coerceImageUrl(item.image_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800"
                          title="Open image in new tab"
                        >
                          View
                        </a>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">-</span>
                    )}
                  </td>
                  <td>
                    {item.notes || '-'}
                  </td>
                  <td className="actions actions-col">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '6px', verticalAlign: 'middle' }}>
                      <CareReviewBadge item={item} onNeedsReviewClick={() => startEditing(item)} />
                      <button
                        onClick={() => setExpandedCareItemId(expandedCareItemId === item.id ? null : item.id)}
                        title="Show / hide care details"
                        style={{
                          border: '1px solid #d1d5db',
                          background: 'white',
                          color: '#374151',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {expandedCareItemId === item.id ? '▴' : '▾'}
                      </button>
                    </div>
                    <button className="edit-button" onClick={() => startEditing(item)} title="Edit row">
                      ✏️
                    </button>
                    <button
                      className="library-button"
                      onClick={() => {
                        if (!item.plant_id) return;
                        navigate(`/plants?plantId=${encodeURIComponent(item.plant_id)}`);
                      }}
                      disabled={!item.plant_id}
                      title={item.plant_id ? 'Edit this plant in your Plant Library' : 'This row is not linked to a Plant Library record'}
                    >
                      🌿
                    </button>
                    <button
                      onClick={() => aiFillItem(item.id)}
                      disabled={aiFillingItemId === item.id || aiBulkFilling}
                      title={
                        item.ai_filled_at
                          ? `Garden App care info filled ${new Date(item.ai_filled_at).toLocaleDateString()} — click to refresh care and seasonal details for this plant`
                          : 'Generate Garden App care and seasonal details for this plant'
                      }
                      style={{
                        border: '1px solid #c4b5fd',
                        background: item.ai_filled_at ? '#ede9fe' : '#f5f3ff',
                        color: '#5b21b6',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        cursor: aiFillingItemId === item.id || aiBulkFilling ? 'not-allowed' : 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      {aiFillingItemId === item.id ? '⏳' : '✨'}
                    </button>
                    <button className="delete-button" onClick={() => deleteItem(item.id)} title="Delete row">
                      🗑️
                    </button>
                  </td>
                </tr>
                {expandedCareItemId === item.id && (
                  <tr id={`mpl-care-${item.id}`}>
                    <td colSpan={100} style={{ background: '#fafafa', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      <CareDetailPanel
                        item={item}
                        onEdit={() => startEditing(item)}
                        onMarkReviewed={() => markCareReviewed(item.id)}
                        markingReviewed={careReviewingItemId === item.id}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk symbol modal */}
      {showBulkSymbolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Bulk set symbol</h3>
                <div className="text-sm text-gray-600">
                  Applies to{' '}
                  <span className="font-semibold">
                    {bulkSymbolScope === 'filtered' ? visibleItems.length : selectedItems.size}
                  </span>{' '}
                  {bulkSymbolScope === 'filtered' ? 'shown (filtered)' : 'selected'} row(s). Overwrites existing symbol settings.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkSymbolModal(false)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Scope</label>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="bulkSymbolScope"
                    value="selected"
                    checked={bulkSymbolScope === 'selected'}
                    onChange={() => setBulkSymbolScope('selected')}
                  />
                  Selected ({selectedItems.size})
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="bulkSymbolScope"
                    value="filtered"
                    checked={bulkSymbolScope === 'filtered'}
                    onChange={() => setBulkSymbolScope('filtered')}
                  />
                  Shown / filtered ({visibleItems.length})
                </label>
              </div>
              {bulkSymbolScope === 'filtered' && visibleItems.length === items.length && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  You are about to apply to all rows because no filters are active.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol</label>
                <select
                  value={bulkSymbol}
                  onChange={(e) => setBulkSymbol(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {allSymbolOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Symbol size ({projectUnits})
                </label>
                <input
                  type="number"
                  value={bulkSymbolSizeDisplay}
                  onChange={(e) => setBulkSymbolSizeDisplay(e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Stored canonically in inches; Metric is converted automatically.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowBulkSymbolModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={bulkApplyingSymbol}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={bulkApplySymbolToSelected}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-400"
                disabled={
                  bulkApplyingSymbol ||
                  (bulkSymbolScope === 'filtered' ? visibleItems.length === 0 : selectedItems.size === 0)
                }
              >
                {bulkApplyingSymbol
                  ? 'Applying…'
                  : `Apply to ${bulkSymbolScope === 'filtered' ? 'shown' : 'selected'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal (keeps table layout manageable) */}
      {editingModalItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl p-6 overflow-y-auto max-h-[95vh]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Edit Project Plant</h3>
                <div className="text-sm text-gray-600">
                  {editingModalItem.common_name}{editingModalItem.botanical_name ? ` • ${editingModalItem.botanical_name}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={cancelEditing}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Common name</label>
                <input
                  type="text"
                  value={editValues.common_name ?? editingModalItem.common_name ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, common_name: e.target.value })}
                  placeholder="Common name"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Botanical name</label>
                <input
                  type="text"
                  value={editValues.botanical_name ?? editingModalItem.botanical_name ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, botanical_name: e.target.value })}
                  placeholder="Botanical name"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  value={editValues.quantity ?? editingModalItem.quantity ?? 0}
                  onChange={(e) => setEditValues({ ...editValues, quantity: parseInt(e.target.value, 10) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editValues.status || editingModalItem.status || 'pending'}
                  onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="quoted">💬 Quoted</option>
                  <option value="ordered">🛒 Ordered</option>
                  <option value="received">📦 Received</option>
                  <option value="complete">✅ Complete</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pot size</label>
                <input
                  type="text"
                  value={editValues.pot_size ?? editingModalItem.pot_size ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, pot_size: e.target.value })}
                  placeholder="#5"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colour</label>
                <input
                  type="text"
                  value={editValues.color ?? editingModalItem.color ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, color: e.target.value })}
                  placeholder="Colour"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={editValues.code ?? editingModalItem.code ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, code: e.target.value })}
                  placeholder="Code"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit price</label>
                <input
                  type="number"
                  value={editValues.unit_price ?? editingModalItem.unit_price ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, unit_price: e.target.value === '' ? '' : Number(e.target.value) })}
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                <input
                  type="number"
                  value={editValues.width ?? editingModalItem.width ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, width: e.target.value === '' ? '' : Number(e.target.value) })}
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                <input
                  type="number"
                  value={editValues.height ?? editingModalItem.height ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, height: e.target.value === '' ? '' : Number(e.target.value) })}
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exposure</label>
                <input
                  type="text"
                  value={editValues.exposure ?? editingModalItem.exposure ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, exposure: e.target.value })}
                  placeholder="e.g. Full sun"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <input
                  type="text"
                  value={editValues.plant_type ?? editingModalItem.plant_type ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, plant_type: e.target.value })}
                  placeholder="e.g. Shrub"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordered</label>
                <input
                  type="number"
                  value={editValues.quantity_ordered ?? editingModalItem.quantity_ordered ?? 0}
                  onChange={(e) => setEditValues({ ...editValues, quantity_ordered: parseInt(e.target.value, 10) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Received</label>
                <input
                  type="number"
                  value={editValues.quantity_received ?? editingModalItem.quantity_received ?? 0}
                  onChange={(e) => setEditValues({ ...editValues, quantity_received: parseInt(e.target.value, 10) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planted</label>
                <input
                  type="number"
                  value={editValues.quantity_planted ?? editingModalItem.quantity_planted ?? 0}
                  onChange={(e) => setEditValues({ ...editValues, quantity_planted: parseInt(e.target.value, 10) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="url"
                    value={editValues.image_url ?? editingModalItem.image_url ?? ''}
                    onChange={(e) => setEditValues({ ...editValues, image_url: e.target.value })}
                    placeholder="https://…"
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const b = editingModalItem.botanical_name || '';
                      const c = editingModalItem.common_name || '';
                      fetchEditPhotos({ botanicalName: b, commonName: c, itemId: editingModalItem.id });
                    }}
                    className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
                    disabled={editPhotosLoading}
                    title="Find recommended photos"
                  >
                    {editPhotosLoading && editPhotoPickerOpenFor === editingModalItem.id ? 'Finding…' : 'Find photos'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openGoogleImages(editingModalItem.botanical_name || editingModalItem.common_name)}
                    className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
                    title="Google Images"
                  >
                    Google
                  </button>
                </div>

                <div className="text-xs text-gray-600 mt-2">
                  Tip: changing the image here updates <span className="font-semibold">this project only</span>.
                </div>
                <div className="mt-2 flex flex-col gap-1">
                  <label className="text-xs text-gray-700 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editValues.propagate_image_to_library}
                      disabled={!((editValues.image_url || '').toString().trim())}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditValues((prev) => ({
                          ...prev,
                          propagate_image_to_library: checked,
                          overwrite_library_image: checked ? !!prev.overwrite_library_image : false
                        }));
                      }}
                      title="Also update the same plant&apos;s image on your Master Plant List (catalog across all projects)"
                    />
                    Also update my Plant Library image
                  </label>

                  <label className="text-xs text-gray-700 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!editValues.overwrite_library_image}
                      disabled={!editValues.propagate_image_to_library}
                      onChange={(e) => setEditValues((prev) => ({ ...prev, overwrite_library_image: e.target.checked }))}
                      title="If checked, replaces the library image even if it already has one"
                    />
                    Replace library image (override)
                  </label>
                </div>

                {editPhotosError && editPhotoPickerOpenFor === editingModalItem.id && (
                  <div className="text-xs" style={{ color: '#b91c1c', marginTop: '6px' }}>
                    {editPhotosError}
                  </div>
                )}

                {editPhotoOptions.length > 0 && editPhotoPickerOpenFor === editingModalItem.id && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '8px' }}>
                      {editPhotoOptions.slice(0, 12).map((photo, idx) => (
                        <button
                          key={`${photo.url}-${idx}`}
                          type="button"
                          onClick={() => selectEditPhoto(photo)}
                          className="border rounded overflow-hidden hover:shadow-sm"
                          style={{
                            borderColor: (editSelectedPhotoUrl && photo.url === editSelectedPhotoUrl) ? '#7c3aed' : '#e5e7eb'
                          }}
                          title={photo.attribution || photo.source || 'Photo'}
                        >
                          <div style={{ width: '100%', height: '64px', background: '#f3f4f6' }}>
                            <img
                              src={photo.thumbnail || photo.url}
                              alt={photo.alt || editingModalItem.common_name || 'Plant'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </div>
                          <div style={{ padding: '2px 4px', fontSize: '10px', color: '#6b7280', textAlign: 'left' }}>
                            {(photo.verified ? 'Verified • ' : '')}{photo.source || ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="sm:col-span-2 rounded-lg border border-green-100 bg-green-50/50 p-4">
                <h4 className="text-base font-semibold text-gray-900 mb-1">Care info</h4>
                <p className="text-xs text-gray-600 mb-4">
                  These fields are used for the expanded care view and homeowner handoff. Leave fields blank if you do not want to include them.
                  Use <span className="font-semibold">Save</span> at the bottom to keep edits. Use <span className="font-semibold">Mark reviewed</span> there when you have finished spot-checking for the Garden App (you can mark before or after Save).
                </p>
                <div className="space-y-5">
                  {CARE_FIELD_GROUPS.map((group) => (
                    <section key={group.title}>
                      <h5 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{group.title}</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {group.fields.map(([key, label]) => {
                          const isLongText = key === 'care_notes' || key.endsWith('_tasks') || key.endsWith('_notes') || key === 'foliage_notes';
                          if (CARE_BOOLEAN_FIELDS.has(key)) {
                            const boolValue = editValues[key] === true ? 'true' : editValues[key] === false ? 'false' : '';
                            return (
                              <div key={key}>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                                <select
                                  value={boolValue}
                                  onChange={(e) => setEditValues({
                                    ...editValues,
                                    [key]: e.target.value === '' ? '' : e.target.value === 'true'
                                  })}
                                  className="w-full px-3 py-2 border rounded-lg bg-white"
                                >
                                  <option value="">Blank</option>
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </select>
                              </div>
                            );
                          }
                          if (isLongText) {
                            return (
                              <div key={key} className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                                <textarea
                                  value={editValues[key] ?? editingModalItem[key] ?? ''}
                                  onChange={(e) => setEditValues({ ...editValues, [key]: e.target.value })}
                                  className="w-full px-3 py-2 border rounded-lg bg-white"
                                  rows={key === 'care_notes' ? 4 : 3}
                                />
                              </div>
                            );
                          }
                          return (
                            <div key={key}>
                              <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                              <input
                                type={CARE_MONTH_FIELDS.has(key) ? 'number' : 'text'}
                                value={editValues[key] ?? editingModalItem[key] ?? ''}
                                onChange={(e) => setEditValues({ ...editValues, [key]: e.target.value })}
                                min={CARE_MONTH_FIELDS.has(key) ? '1' : undefined}
                                max={CARE_MONTH_FIELDS.has(key) ? '12' : undefined}
                                className="w-full px-3 py-2 border rounded-lg bg-white"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Garden App zone</label>
                <select
                  value={editValues.manual_plan_zone_id ?? editingModalItem.manual_plan_zone_id ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, manual_plan_zone_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="">Unassigned (use map placement when available)</option>
                  {planZones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  When set, handoff groups this plant under this zone before map-based detection.
                </p>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editValues.notes ?? editingModalItem.notes ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                  placeholder="Add notes…"
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <CareReviewBadge item={editingModalItem} />
                <span className="text-xs text-gray-600">
                  Save keeps this form. Mark reviewed only sets the list badge (no field changes).
                </span>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => markCareReviewed(editingModalItem.id)}
                  disabled={
                    !editingModalItem.ai_filled_at
                    || !!editingModalItem.care_reviewed_at
                    || careReviewingItemId === editingModalItem.id
                  }
                  className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                    editingModalItem.care_reviewed_at
                      ? 'cursor-default border-green-200 bg-green-100 text-green-800'
                      : 'border-green-300 bg-white text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50'
                  }`}
                  title={
                    !editingModalItem.ai_filled_at
                      ? 'Generate Garden App care with the row sparkle (AI) first, then you can mark reviewed.'
                      : editingModalItem.care_reviewed_at
                        ? 'This plant care is already marked reviewed.'
                        : 'Mark this plant’s care info as designer-reviewed for the Garden App handoff.'
                  }
                >
                  {careReviewingItemId === editingModalItem.id
                    ? 'Saving…'
                    : editingModalItem.care_reviewed_at
                      ? 'Reviewed'
                      : 'Mark reviewed'}
                </button>
                <button
                  type="button"
                  onClick={() => saveEditing(editingModalItem.id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default MasterPlantList;
